/**
 * Editor Page — Full-screen visual editor inspired by Elementor/Webflow.
 *
 * Reuses ALL the backend work from Phases 1-3:
 *   • GET  /api/client/:token/ast       — full AST + stats
 *   • GET  /api/client/:token/blocks    — preset catalog
 *   • PATCH /api/client/:token/ast      — 7 vetted ops
 *   • POST /api/client/:token/ai-suggest-style  — 3 style variants
 *
 * Layout (matches user-supplied mockup):
 *   ┌─────────────────────────────────────────────────────────────┐
 *   │ HEADER: logo · undo/redo · Vista Previa · Publicar · user   │
 *   ├──────┬────────────────────────────────────┬─────────────────┤
 *   │ L    │                                    │ PROPIEDADES     │
 *   │ I    │                                    │ [Estilo][Cfg][C]│
 *   │ C    │        iframe preview              │  TIPOGRAFÍA     │
 *   │ O    │     (click selects element)        │  COLORES        │
 *   │ N    │                                    │  OPACIDAD       │
 *   │ S    │                                    │  DISTRIBUCIÓN   │
 *   │      │                                    │  [Avanzado]     │
 *   └──────┴────────────────────────────────────┴─────────────────┘
 *                              ⚡ floating AI button
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Undo2, Redo2, Eye, Rocket, HelpCircle, Bell, User as UserIcon, X,
  Sparkles, LayoutGrid, Columns3, Type as TypeIcon, Image as ImageIcon,
  MousePointerClick, FormInput, Video, Menu as MenuIcon, Pen,
  Loader2, ChevronRight, ChevronDown, Plus, Check,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Copy, Trash2,
  GripVertical, Wand2, Monitor, Tablet, Smartphone, Send, Bot,
  TrendingUp as TrendingUpIcon, Globe as GlobeIcon, Code2 as Code2Icon,
  Crown as CrownIcon, Gift as GiftIcon, Move as MoveIcon,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { EmbeddedAIChat, AIChatBubble } from "../components/editor/EmbeddedAIChat";
import { ClientNavSidebar } from "../components/editor/ClientNavSidebar";
import { useResizableSplit } from "./ClientDashboard.useResizableSplit";
import { attachGrapesResizer } from "../utils/grapesResizer";
import { findPosition, collectDims } from "../utils/grapesFindPosition";
import morphdom from "morphdom";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Web-safe font list shown in the "Fuente" dropdown.
const FONTS = [
  "Plus Jakarta Sans", "Inter", "Poppins", "Montserrat", "Roboto", "Playfair Display",
  "Outfit", "Space Grotesk", "DM Sans", "Manrope", "Work Sans", "Source Sans Pro",
];

const WEIGHT_OPTIONS = [
  { v: "",    l: "Default" },
  { v: "300", l: "300 Light" },
  { v: "400", l: "400 Regular" },
  { v: "500", l: "500 Medium" },
  { v: "600", l: "600 Semibold" },
  { v: "700", l: "700 Bold" },
  { v: "800", l: "800 Extrabold" },
  { v: "900", l: "900 Black" },
];

// Left sidebar categories → each opens a drawer with draggable/clickable blocks.
// Sections (full layout pieces) live under SECCIONES. Atomic blocks live in
// their dedicated category so a user clicking BOTÓN gets buttons, not a CTA section.
const CATEGORIES = [
  { id: "sections", label: "SECCIONES", Icon: LayoutGrid,         blocks: ["hero", "cta", "text_block", "card_grid", "gallery", "image", "video", "menu", "form"] },
  { id: "columns",  label: "COLUMNAS",  Icon: Columns3,            blocks: ["card_grid"] },
  { id: "text",     label: "TEXTO",     Icon: TypeIcon,            blocks: ["heading", "subheading", "paragraph", "list_bullet"] },
  { id: "image",    label: "IMAGEN",    Icon: ImageIcon,           blocks: ["image_simple", "image", "gallery"] },
  { id: "button",   label: "BOTÓN",     Icon: MousePointerClick,   blocks: ["button_primary", "button_outline", "link"] },
  { id: "form",     label: "FORMULARIO",Icon: FormInput,           blocks: ["input_text", "form"] },
  { id: "video",    label: "VIDEO",     Icon: Video,               blocks: ["video"] },
  { id: "menu",     label: "MENÚ",      Icon: MenuIcon,            blocks: ["menu"] },
  { id: "extras",   label: "EXTRAS",    Icon: Plus,                blocks: ["divider", "spacer"] },
];

// ─── Parse/serialize inline styles ───────────────────────────────────────────
const parseStyle = (s) => {
  const out = {};
  (s || "").split(";").forEach(p => {
    const [k, v] = p.split(":").map(x => x?.trim());
    if (k && v) out[k] = v;
  });
  return out;
};

const DEVICES = [
  { id: "desktop", label: "Escritorio", Icon: Monitor,    width: "100%",  maxWidth: 1200 },
  { id: "tablet",  label: "Tablet",     Icon: Tablet,     width: "768px", maxWidth: 768  },
  { id: "mobile",  label: "Móvil",      Icon: Smartphone, width: "375px", maxWidth: 375  },
];

// ─── Header ──────────────────────────────────────────────────────────────────
function EditorHeader({ onPublish, publishing, onPreview, onUndo, onRedo, canUndo, canRedo, device, setDevice }) {
  return (
    <header className="flex items-center justify-between px-4 py-2.5 bg-[#0A0A0A] border-b border-white/5" data-testid="editor-header">
      <div className="flex items-center gap-3">
        <span className="text-[11px] font-bold text-gray-400 tracking-[0.18em] uppercase">Editor</span>
        <div className="flex items-center gap-2 pl-3 border-l border-white/10">
          <button onClick={onUndo} disabled={!canUndo} data-testid="editor-undo-btn"
            title="Deshacer (⌘+Z)"
            className={`flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded transition-colors ${
              canUndo ? "text-gray-300 hover:bg-white/5 hover:text-white" : "text-gray-600 cursor-not-allowed"
            }`}>
            <Undo2 size={12} /> Deshacer
          </button>
          <button onClick={onRedo} disabled={!canRedo} data-testid="editor-redo-btn"
            title="Rehacer (⌘+⇧+Z)"
            className={`flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded transition-colors ${
              canRedo ? "text-gray-300 hover:bg-white/5 hover:text-white" : "text-gray-600 cursor-not-allowed"
            }`}>
            <Redo2 size={12} /> Rehacer
          </button>
        </div>
      </div>
      {/* Center: device toggle (responsive testing) */}
      <div className="flex items-center gap-0.5 bg-white/5 rounded-lg p-0.5" data-testid="device-toggle">
        {DEVICES.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setDevice(id)}
            data-testid={`device-${id}`}
            title={label}
            className={`flex items-center gap-1 px-2.5 py-1 text-[10.5px] font-bold rounded-md transition-all ${
              device === id ? "bg-[#ffc700] text-black" : "text-gray-400 hover:text-white"
            }`}>
            <Icon size={12} /> {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onPreview} data-testid="editor-preview-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold text-[#ffc700] border border-[#ffc700]/40 rounded-lg hover:bg-[#ffc700]/10">
          <Eye size={12} /> Vista Previa
        </button>
        <button onClick={onPublish} disabled={publishing} data-testid="editor-publish-btn"
          className="flex items-center gap-1.5 px-4 py-1.5 text-[11px] font-black text-black bg-[#ffc700] rounded-lg hover:bg-yellow-400 disabled:opacity-50">
          {publishing ? <Loader2 size={12} className="animate-spin" /> : <Rocket size={12} />} Publicar
        </button>
        <button className="w-7 h-7 rounded-md hover:bg-white/5 flex items-center justify-center text-gray-400"><HelpCircle size={14}/></button>
        <button className="w-7 h-7 rounded-md hover:bg-white/5 flex items-center justify-center text-gray-400"><Bell size={14}/></button>
        <div className="w-7 h-7 rounded-full bg-[#ffc700]/20 border border-[#ffc700]/40 flex items-center justify-center text-[#ffc700]"><UserIcon size={13}/></div>
      </div>
    </header>
  );
}

// ─── Left sidebar with icons ────────────────────────────────────────────────
// Tooltip used by the editor blocks sidebar.
function SidebarTip({ label, children }) {
  return (
    <div className="relative group/tip">
      {children}
      <span className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2.5 py-1.5 bg-[#1A1A1A] border border-white/10 text-white text-[11px] font-semibold rounded-lg whitespace-nowrap shadow-xl opacity-0 translate-x-1 group-hover/tip:opacity-100 group-hover/tip:translate-x-0 transition-all duration-150 z-[60]">
        {label}
      </span>
    </div>
  );
}

// ─── Editor blocks sidebar (categories) ────────────────────────────────────
// Sits to the right of the client navigation sidebar. NO logo here (the logo
// lives only at the very top-left in ClientNavSidebar).
function EditorBlocksSidebar({ activeCat, onCatClick }) {
  return (
    <aside
      className="w-[72px] flex-shrink-0 flex flex-col items-center py-3 border-r"
      style={{ background: "#0f0f0f", borderColor: "rgba(255,255,255,0.06)" }}
      data-testid="editor-blocks-sidebar"
    >
      <span className="text-[8.5px] font-black text-gray-600 tracking-[0.16em] uppercase mb-2">Bloques</span>
      <div className="w-8 h-px mb-2" style={{ background: "rgba(255,255,255,0.08)" }} />
      <nav className="flex flex-col gap-1 flex-1">
        {CATEGORIES.map(({ id, label, Icon }) => {
          const active = activeCat === id;
          return (
            <SidebarTip key={id} label={label}>
              <button
                onClick={() => onCatClick(id)}
                data-testid={`left-cat-${id}`}
                className={`flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-150 ${
                  active
                    ? "text-[#FFC107] bg-[#FFC107]/[0.12]"
                    : "text-gray-500 hover:text-white hover:bg-white/[0.08]"
                }`}
              >
                <Icon size={18} />
              </button>
            </SidebarTip>
          );
        })}
      </nav>
    </aside>
  );
}

// ─── Drawer with blocks for the chosen category ─────────────────────────────
function BlocksDrawer({ category, blocks, onInsert, onClose, offsetLeft = 0 }) {
  if (!category) return null;
  const cat = CATEGORIES.find(c => c.id === category);
  const avail = blocks.filter(b => cat?.blocks.includes(b.key));
  return (
    <div
      className="bg-[#141414] border-r border-white/5 flex flex-col absolute top-0 bottom-0 z-30"
      style={{ width: 260, left: offsetLeft }}
      data-testid="blocks-drawer"
    >
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/5">
        <h3 className="text-[11px] font-black text-white uppercase tracking-wider">{cat?.label}</h3>
        <button onClick={onClose} className="w-6 h-6 rounded hover:bg-white/10 flex items-center justify-center text-gray-400"><X size={12}/></button>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        <p className="text-[9.5px] text-gray-500 text-center pb-1">
          Click para insertar al final · arrastrá al canvas para soltar donde quieras
        </p>
        {avail.length === 0 ? (
          <p className="text-[10px] text-gray-500 text-center py-6 px-3">
            Sin bloques disponibles aún en esta categoría. Próximamente más plantillas.
          </p>
        ) : avail.map(b => (
          <button
            key={b.key}
            onClick={() => onInsert(b.key)}
            draggable="true"
            onDragStart={(e) => {
              e.dataTransfer.setData("application/sy-block", b.key);
              e.dataTransfer.effectAllowed = "copy";
            }}
            data-testid={`drawer-block-${b.key}`}
            className="w-full text-left p-2.5 rounded-lg bg-white/[0.03] hover:bg-[#ffc700]/10 hover:border-[#ffc700]/40 border border-white/5 transition-all active:scale-[0.99] cursor-grab active:cursor-grabbing"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded bg-[#ffc700]/20 text-[#ffc700] flex items-center justify-center"><Plus size={11}/></span>
              <span className="text-[11px] font-bold text-white">{b.label}</span>
            </div>
            <p className="text-[9.5px] text-gray-400 leading-tight">{b.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Style Tab ───────────────────────────────────────────────────────────────
function StyleSection({ title, children }) {
  return (
    <div>
      <h4 className="text-[9.5px] font-black text-gray-400 uppercase tracking-wider mb-2">{title}</h4>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] text-gray-400 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function ColorField({ value, onChange, presets = ["#FFC107", "#0A0A0A", "#FFFFFF", "#EF4444", "#10B981", "#3B82F6"] }) {
  const hex = value?.match(/#[0-9a-f]{6}/i)?.[0] || "";
  const displayLabel = hex ? hex.toUpperCase() : (value || "—");
  return (
    <div className="flex items-center gap-1.5 p-1.5 bg-[#1A1A1A] border border-white/5 rounded-lg">
      <label className="w-7 h-7 rounded-md border border-white/10 overflow-hidden cursor-pointer flex-shrink-0"
        style={{ background: value || "#000" }}>
        <input type="color" value={hex || "#000000"} onChange={e => onChange(e.target.value)}
          className="w-full h-full opacity-0 cursor-pointer" />
      </label>
      <span className="text-[10.5px] font-bold text-white flex-1 truncate">{displayLabel}</span>
      <div className="flex gap-0.5">
        {presets.map(p => (
          <button key={p} onClick={() => onChange(p)}
            className="w-4 h-4 rounded-sm border border-white/10 hover:scale-110 transition-transform"
            style={{ background: p }} />
        ))}
      </div>
    </div>
  );
}

function StyleTab({ node, draftStyle, setDraftStyle, onSuggestClick }) {
  if (!node) return <EmptyState msg="Seleccioná un elemento en el canvas" Icon={Pen} />;
  const set = (k, v) => setDraftStyle({ ...draftStyle, [k]: v });
  const textAlign = draftStyle["text-align"] || "";
  const opacity = parseFloat(draftStyle["opacity"] || "1");

  return (
    <div className="space-y-5" data-testid="inspector-style-tab">
      <button onClick={onSuggestClick} data-testid="ai-suggest-style-btn"
        className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-bold rounded-lg transition-all active:scale-95"
        style={{ background: "linear-gradient(135deg, #3D1E6B 0%, #5B2790 100%)", color: "#DDD6FE", border: "1px solid #7C3AED" }}>
        <Wand2 size={11} /> Sugerir con IA
        <span className="ml-1 text-[8.5px] font-black px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300">GRATIS</span>
      </button>

      <StyleSection title="Tipografía">
        <Field label="Fuente">
          <select value={draftStyle["font-family"]?.replace(/['"]/g, "") || ""}
            onChange={e => set("font-family", e.target.value ? `"${e.target.value}", sans-serif` : "")}
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white"
            data-testid="style-input-font-family">
            <option value="">— default —</option>
            {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Tamaño">
            <input type="text" value={draftStyle["font-size"] || ""}
              onChange={e => set("font-size", e.target.value)} placeholder="24px"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="style-input-font-size" />
          </Field>
          <Field label="Peso">
            <select value={draftStyle["font-weight"] || ""}
              onChange={e => set("font-weight", e.target.value)}
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white"
              data-testid="style-input-font-weight">
              {WEIGHT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        </div>
      </StyleSection>

      <StyleSection title="Colores & Fondo">
        <Field label="Color Principal">
          <ColorField value={draftStyle["color"] || ""} onChange={v => set("color", v)} />
        </Field>
        <Field label="Fondo">
          <ColorField value={draftStyle["background"] || ""} onChange={v => set("background", v)} />
        </Field>
      </StyleSection>

      <StyleSection title="Opacidad">
        <div className="flex items-center gap-2">
          <input type="range" min="0" max="1" step="0.05"
            value={Number.isNaN(opacity) ? 1 : opacity}
            onChange={e => set("opacity", e.target.value === "1" ? "" : e.target.value)}
            className="flex-1 accent-[#ffc700]"
            data-testid="style-input-opacity" />
          <span className="text-[11px] font-mono text-white w-10 text-right">
            {Math.round((Number.isNaN(opacity) ? 1 : opacity) * 100)}%
          </span>
        </div>
      </StyleSection>

      <StyleSection title="Distribución">
        <div className="grid grid-cols-4 gap-1">
          {[
            { v: "left",    Icon: AlignLeft },
            { v: "center",  Icon: AlignCenter },
            { v: "right",   Icon: AlignRight },
            { v: "justify", Icon: AlignJustify },
          ].map(({ v, Icon }) => (
            <button key={v} onClick={() => set("text-align", textAlign === v ? "" : v)}
              data-testid={`align-${v}`}
              className={`py-2.5 rounded-lg border transition-colors ${
                textAlign === v ? "bg-[#3B82F6]/20 border-[#3B82F6] text-[#3B82F6]" : "bg-[#1A1A1A] border-white/5 text-gray-400 hover:text-white"
              }`}>
              <Icon size={14} className="mx-auto" />
            </button>
          ))}
        </div>
      </StyleSection>

      <StyleSection title="Espaciado">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Padding">
            <input type="text" value={draftStyle["padding"] || ""}
              onChange={e => set("padding", e.target.value)} placeholder="16px 24px"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="style-input-padding" />
          </Field>
          <Field label="Margen">
            <input type="text" value={draftStyle["margin"] || ""}
              onChange={e => set("margin", e.target.value)} placeholder="0 auto"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="style-input-margin" />
          </Field>
        </div>
        <Field label="Radio de borde">
          <input type="text" value={draftStyle["border-radius"] || ""}
            onChange={e => set("border-radius", e.target.value)} placeholder="12px"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="style-input-border-radius" />
        </Field>
      </StyleSection>
    </div>
  );
}

// ─── Config Tab ──────────────────────────────────────────────────────────────
function ConfigTab({ node, draftAttrs, setDraftAttrs }) {
  const allowedKeys = useMemo(() => {
    if (!node) return [];
    const base = ["id", "class"];
    if (node.type === "image")   return [...base, "src", "alt", "title", "loading"];
    if (node.type === "link")    return [...base, "href", "target", "rel", "title"];
    if (node.type === "button")  return [...base, "type", "title", "aria-label"];
    if (node.type === "input")   return [...base, "name", "type", "placeholder", "value"];
    if (node.type === "form")    return [...base, "action", "method"];
    return base;
  }, [node]);
  if (!node) return <EmptyState msg="Seleccioná un elemento en el canvas" Icon={Pen} />;
  const set = (k, v) => setDraftAttrs({ ...draftAttrs, [k]: v });
  return (
    <div className="space-y-3" data-testid="inspector-config-tab">
      <div className="text-[10px] text-gray-500 bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-1.5">
        <code className="font-mono text-[#ffc700]">&lt;{node.tag}&gt;</code> · {node.type}
      </div>
      {allowedKeys.map(k => (
        <Field key={k} label={k}>
          <input type="text" value={draftAttrs[k] ?? node.attrs[k] ?? ""}
            onChange={e => set(k, e.target.value)} placeholder={node.attrs[k] || "—"}
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid={`config-input-${k}`} />
        </Field>
      ))}
    </div>
  );
}

// ─── Editar Tab — comprehensive Framer-style style manager ───────────────────
// Reuses the same draft-style/draft-attrs flow as StyleTab + ConfigTab so
// the "Aplicar cambios" button at the bottom commits the whole batch via
// the existing AST patch endpoint.
function EditarTab({ node, draftStyle, setDraftStyle, draftAttrs, setDraftAttrs }) {
  if (!node) return <EmptyState msg="Seleccioná un elemento en el canvas" Icon={Pen} />;
  const setS = (k, v) => setDraftStyle({ ...draftStyle, [k]: v });
  const setA = (k, v) => setDraftAttrs({ ...draftAttrs, [k]: v });
  const opacity = parseFloat(draftStyle["opacity"] || "1");

  return (
    <div className="space-y-5" data-testid="inspector-editar-tab">
      <div className="text-[10px] text-gray-500 bg-[#1A1A1A] border border-white/5 rounded-lg px-2 py-1.5 flex items-center gap-2">
        <code className="font-mono text-[#ffc700]">&lt;{node.tag}&gt;</code>
        <span>·</span>
        <span>{node.type}</span>
      </div>

      {/* ─── Layout ─────────────────────────────────────────────────────── */}
      <StyleSection title="Layout">
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { v: "block",        l: "Block" },
            { v: "flex",         l: "Flex" },
            { v: "inline-block", l: "Inline" },
            { v: "grid",         l: "Grid" },
            { v: "inline",       l: "Línea" },
            { v: "none",         l: "Oculto" },
          ].map(o => (
            <button key={o.v} onClick={() => setS("display", draftStyle["display"] === o.v ? "" : o.v)}
              data-testid={`editar-display-${o.v}`}
              className={`py-1.5 text-[10.5px] font-bold rounded-md border transition-colors ${
                draftStyle["display"] === o.v
                  ? "bg-[#3B82F6]/20 border-[#3B82F6] text-[#3B82F6]"
                  : "bg-[#1A1A1A] border-white/5 text-gray-400 hover:text-white"
              }`}>{o.l}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Ancho">
            <input type="text" value={draftStyle["width"] || ""} onChange={e => setS("width", e.target.value)}
              placeholder="auto · 100% · 320px"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-width" />
          </Field>
          <Field label="Alto">
            <input type="text" value={draftStyle["height"] || ""} onChange={e => setS("height", e.target.value)}
              placeholder="auto · 200px"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-height" />
          </Field>
        </div>
      </StyleSection>

      {/* ─── Espaciado ─────────────────────────────────────────────────── */}
      <StyleSection title="Espaciado">
        <Field label="Padding (interior)">
          <input type="text" value={draftStyle["padding"] || ""}
            onChange={e => setS("padding", e.target.value)} placeholder="16px 24px"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-padding" />
        </Field>
        <Field label="Margen (exterior)">
          <input type="text" value={draftStyle["margin"] || ""}
            onChange={e => setS("margin", e.target.value)} placeholder="0 auto"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-margin" />
        </Field>
        <Field label="Espacio entre hijos (gap)">
          <input type="text" value={draftStyle["gap"] || ""}
            onChange={e => setS("gap", e.target.value)} placeholder="12px"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-gap" />
        </Field>
      </StyleSection>

      {/* ─── Tipografía ─────────────────────────────────────────────────── */}
      <StyleSection title="Tipografía">
        <Field label="Fuente">
          <select value={draftStyle["font-family"]?.replace(/['"]/g, "") || ""}
            onChange={e => setS("font-family", e.target.value ? `"${e.target.value}", sans-serif` : "")}
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white"
            data-testid="editar-input-font-family">
            <option value="">— default —</option>
            {FONTS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Tamaño">
            <input type="text" value={draftStyle["font-size"] || ""}
              onChange={e => setS("font-size", e.target.value)} placeholder="24px"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-font-size" />
          </Field>
          <Field label="Peso">
            <select value={draftStyle["font-weight"] || ""}
              onChange={e => setS("font-weight", e.target.value)}
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white"
              data-testid="editar-input-font-weight">
              {WEIGHT_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Interlineado">
            <input type="text" value={draftStyle["line-height"] || ""}
              onChange={e => setS("line-height", e.target.value)} placeholder="1.5"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-line-height" />
          </Field>
          <Field label="Espaciado letras">
            <input type="text" value={draftStyle["letter-spacing"] || ""}
              onChange={e => setS("letter-spacing", e.target.value)} placeholder="0.02em"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-letter-spacing" />
          </Field>
        </div>
        <Field label="Alineación">
          <div className="grid grid-cols-4 gap-1">
            {[
              { v: "left",    Icon: AlignLeft },
              { v: "center",  Icon: AlignCenter },
              { v: "right",   Icon: AlignRight },
              { v: "justify", Icon: AlignJustify },
            ].map(({ v, Icon }) => (
              <button key={v} onClick={() => setS("text-align", draftStyle["text-align"] === v ? "" : v)}
                data-testid={`editar-align-${v}`}
                className={`py-2 rounded-lg border transition-colors ${
                  draftStyle["text-align"] === v ? "bg-[#3B82F6]/20 border-[#3B82F6] text-[#3B82F6]" : "bg-[#1A1A1A] border-white/5 text-gray-400 hover:text-white"
                }`}>
                <Icon size={13} className="mx-auto" />
              </button>
            ))}
          </div>
        </Field>
      </StyleSection>

      {/* ─── Color ───────────────────────────────────────────────────────── */}
      <StyleSection title="Color">
        <Field label="Color de texto">
          <ColorField value={draftStyle["color"] || ""} onChange={v => setS("color", v)} />
        </Field>
        <Field label="Fondo">
          <ColorField value={draftStyle["background"] || ""} onChange={v => setS("background", v)} />
        </Field>
      </StyleSection>

      {/* ─── Efectos (border, radius, shadow, opacity) ─────────────────── */}
      <StyleSection title="Efectos">
        <div className="grid grid-cols-2 gap-2">
          <Field label="Borde">
            <input type="text" value={draftStyle["border"] || ""}
              onChange={e => setS("border", e.target.value)} placeholder="1px solid #fff"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-border" />
          </Field>
          <Field label="Radio">
            <input type="text" value={draftStyle["border-radius"] || ""}
              onChange={e => setS("border-radius", e.target.value)} placeholder="12px"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-border-radius" />
          </Field>
        </div>
        <Field label="Sombra">
          <input type="text" value={draftStyle["box-shadow"] || ""}
            onChange={e => setS("box-shadow", e.target.value)} placeholder="0 8px 24px rgba(0,0,0,0.2)"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-box-shadow" />
        </Field>
        <Field label="Filtros (blur, brightness…)">
          <input type="text" value={draftStyle["filter"] || ""}
            onChange={e => setS("filter", e.target.value)} placeholder="blur(8px) brightness(1.1)"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-filter" />
        </Field>
        <div>
          <span className="text-[10px] text-gray-400 mb-1 block">Opacidad</span>
          <div className="flex items-center gap-2">
            <input type="range" min="0" max="1" step="0.05"
              value={Number.isNaN(opacity) ? 1 : opacity}
              onChange={e => setS("opacity", e.target.value === "1" ? "" : e.target.value)}
              className="flex-1 accent-[#ffc700]"
              data-testid="editar-input-opacity" />
            <span className="text-[11px] font-mono text-white w-10 text-right">
              {Math.round((Number.isNaN(opacity) ? 1 : opacity) * 100)}%
            </span>
          </div>
        </div>
      </StyleSection>

      {/* ─── Avanzado (atributos + CSS libre) ──────────────────────────── */}
      <StyleSection title="Avanzado">
        <Field label="ID">
          <input type="text" value={draftAttrs["id"] ?? node.attrs["id"] ?? ""}
            onChange={e => setA("id", e.target.value)} placeholder="—"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-attr-id" />
        </Field>
        <Field label="Clases CSS">
          <input type="text" value={draftAttrs["class"] ?? node.attrs["class"] ?? ""}
            onChange={e => setA("class", e.target.value)} placeholder="hero btn-primary"
            className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
            data-testid="editar-input-attr-class" />
        </Field>
        {node.type === "link" && (
          <Field label="Enlace (href)">
            <input type="text" value={draftAttrs["href"] ?? node.attrs["href"] ?? ""}
              onChange={e => setA("href", e.target.value)} placeholder="https://… · #seccion · tel:099…"
              className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
              data-testid="editar-input-attr-href" />
          </Field>
        )}
        {node.type === "image" && (
          <>
            <Field label="Imagen (src)">
              <input type="text" value={draftAttrs["src"] ?? node.attrs["src"] ?? ""}
                onChange={e => setA("src", e.target.value)} placeholder="https://…"
                className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white font-mono"
                data-testid="editar-input-attr-src" />
            </Field>
            <Field label="Texto alternativo (alt)">
              <input type="text" value={draftAttrs["alt"] ?? node.attrs["alt"] ?? ""}
                onChange={e => setA("alt", e.target.value)} placeholder="Descripción para accesibilidad"
                className="w-full px-2 py-2 text-[11px] bg-[#1A1A1A] border border-white/5 rounded-lg text-white"
                data-testid="editar-input-attr-alt" />
            </Field>
          </>
        )}
      </StyleSection>
    </div>
  );
}


function flattenVisible(nodes, expanded, depth = 0, parentId = "", acc = []) {
  for (const n of nodes) {
    acc.push({ node: n, depth, parentId });
    const hasKids = n.children && n.children.length > 0;
    const open = expanded[n.id] !== false;
    if (hasKids && open) flattenVisible(n.children, expanded, depth + 1, n.id, acc);
  }
  return acc;
}

// Map raw HTML tags + AST node types to friendly Spanish labels + icons.
// Visual mode for non-tech users: "Sección", "Título", "Botón" etc.
const NODE_LABELS = {
  section: { label: "Sección",         icon: LayoutGrid,         tone: "text-amber-300" },
  div:     { label: "Contenedor",      icon: Columns3,           tone: "text-gray-300" },
  h1:      { label: "Título principal",icon: TypeIcon,           tone: "text-white" },
  h2:      { label: "Subtítulo",       icon: TypeIcon,           tone: "text-white" },
  h3:      { label: "Encabezado",      icon: TypeIcon,           tone: "text-gray-200" },
  h4:      { label: "Encabezado",      icon: TypeIcon,           tone: "text-gray-200" },
  h5:      { label: "Encabezado",      icon: TypeIcon,           tone: "text-gray-200" },
  h6:      { label: "Encabezado",      icon: TypeIcon,           tone: "text-gray-200" },
  p:       { label: "Texto",           icon: TypeIcon,           tone: "text-gray-400" },
  span:    { label: "Texto",           icon: TypeIcon,           tone: "text-gray-400" },
  a:       { label: "Enlace",          icon: MousePointerClick,  tone: "text-blue-300" },
  button:  { label: "Botón",           icon: MousePointerClick,  tone: "text-emerald-300" },
  img:     { label: "Imagen",          icon: ImageIcon,          tone: "text-pink-300" },
  picture: { label: "Imagen",          icon: ImageIcon,          tone: "text-pink-300" },
  video:   { label: "Video",           icon: Video,              tone: "text-purple-300" },
  iframe:  { label: "Video embebido",  icon: Video,              tone: "text-purple-300" },
  form:    { label: "Formulario",      icon: FormInput,          tone: "text-orange-300" },
  input:   { label: "Campo",           icon: FormInput,          tone: "text-orange-200" },
  textarea:{ label: "Campo de texto",  icon: FormInput,          tone: "text-orange-200" },
  label:   { label: "Etiqueta",        icon: TypeIcon,           tone: "text-gray-400" },
  nav:     { label: "Menú",            icon: MenuIcon,           tone: "text-cyan-300" },
  ul:      { label: "Lista",           icon: MenuIcon,           tone: "text-gray-300" },
  ol:      { label: "Lista numerada",  icon: MenuIcon,           tone: "text-gray-300" },
  li:      { label: "Ítem de lista",   icon: ChevronRight,       tone: "text-gray-400" },
  header:  { label: "Encabezado",      icon: LayoutGrid,         tone: "text-amber-200" },
  footer:  { label: "Pie de página",   icon: LayoutGrid,         tone: "text-amber-200" },
  main:    { label: "Contenido",       icon: LayoutGrid,         tone: "text-amber-300" },
  article: { label: "Artículo",        icon: LayoutGrid,         tone: "text-amber-200" },
  aside:   { label: "Lateral",         icon: LayoutGrid,         tone: "text-amber-200" },
  strong:  { label: "Negrita",         icon: TypeIcon,           tone: "text-gray-300" },
  em:      { label: "Cursiva",         icon: TypeIcon,           tone: "text-gray-300" },
};
const labelForNode = (node) => {
  const tag = (node.tag || "").toLowerCase();
  const meta = NODE_LABELS[tag];
  if (meta) return { label: meta.label, Icon: meta.icon, tone: meta.tone };
  // fallback: use the AST node.type if present, else tag uppercase
  return { label: (node.type || tag || "Elemento").charAt(0).toUpperCase() + (node.type || tag).slice(1), Icon: LayoutGrid, tone: "text-gray-400" };
};

function LayerRow({ node, depth, hasKids, expanded, onToggleExpand, selected, onSelect, onDuplicate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: node.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    paddingLeft: 6 + depth * 12,
  };
  const preview = (node.text || "").replace(/<[^>]+>/g, "").trim().slice(0, 28);
  const { label, Icon, tone } = labelForNode(node);
  return (
    <div ref={setNodeRef} style={style}
      onClick={() => onSelect(node.id)}
      className={`group flex items-center gap-1.5 py-1.5 pr-1.5 rounded-md cursor-pointer text-[11px] ${
        selected ? "bg-[#3B82F6]/15 border border-[#3B82F6]/50" : "hover:bg-white/5 border border-transparent"
      }`}
      data-testid={`layers-node-${node.id}`}>
      <button {...attributes} {...listeners} onClick={e => e.stopPropagation()}
        className="w-3 h-3 text-gray-600 hover:text-gray-300 cursor-grab active:cursor-grabbing flex-shrink-0"
        title="Arrastrar para reordenar">
        <GripVertical size={11}/>
      </button>
      {hasKids ? (
        <button onClick={e => { e.stopPropagation(); onToggleExpand(node.id); }}
          className="w-3.5 h-3.5 text-gray-500 hover:text-white flex-shrink-0">
          {expanded ? <ChevronDown size={11}/> : <ChevronRight size={11}/>}
        </button>
      ) : <span className="w-3.5 h-3.5 flex-shrink-0" />}
      <Icon size={12} className={`flex-shrink-0 ${tone}`} />
      <span className={`font-bold ${selected ? "text-white" : tone} flex-shrink-0`}>{label}</span>
      {preview && <span className="text-gray-500 truncate flex-1 italic">"{preview}"</span>}
      <span className="hidden group-hover:flex gap-0.5">
        <button onClick={e => { e.stopPropagation(); onDuplicate(node.id); }}
          className="w-5 h-5 rounded hover:bg-blue-500/20 text-gray-400 hover:text-blue-300 flex items-center justify-center"
          title="Duplicar">
          <Copy size={10}/>
        </button>
        <button onClick={e => { e.stopPropagation(); onDelete(node.id); }}
          className="w-5 h-5 rounded hover:bg-red-500/20 text-gray-400 hover:text-red-300 flex items-center justify-center"
          title="Eliminar">
          <Trash2 size={10}/>
        </button>
      </span>
    </div>
  );
}

function LayersTab({ ast, selectedId, onSelect, expanded, toggleExpand, onReorder, onDuplicate, onDelete }) {
  const flat = useMemo(() => flattenVisible(ast?.nodes || [], expanded), [ast, expanded]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const allIds = flat.map(r => r.node.id);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = flat.find(r => r.node.id === active.id);
    const to = flat.find(r => r.node.id === over.id);
    if (!from || !to || from.parentId !== to.parentId) return;
    const sibs = flat.filter(r => r.parentId === from.parentId).map(r => r.node.id);
    const fi = sibs.indexOf(active.id);
    const ti = sibs.indexOf(over.id);
    if (fi === -1 || ti === -1) return;
    const next = [...sibs]; next.splice(fi, 1); next.splice(ti, 0, active.id);
    onReorder(from.parentId, next);
  };

  return (
    <div data-testid="inspector-layers-tab">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={allIds} strategy={verticalListSortingStrategy}>
          <div className="space-y-[1px]">
            {flat.map(({ node, depth }) => {
              const hasKids = node.children && node.children.length > 0;
              const open = expanded[node.id] !== false;
              return (
                <LayerRow key={node.id} node={node} depth={depth} hasKids={hasKids}
                  expanded={open} onToggleExpand={toggleExpand}
                  selected={node.id === selectedId} onSelect={onSelect}
                  onDuplicate={onDuplicate} onDelete={onDelete} />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

function EmptyState({ msg, Icon }) {
  return (
    <div className="text-center py-14 px-4">
      <Icon size={28} className="mx-auto text-gray-600 mb-2" />
      <p className="text-[11px] text-gray-500">{msg}</p>
    </div>
  );
}

// ─── AI Suggest Modal ────────────────────────────────────────────────────────
function StyleSuggestModal({ node, token, onClose, onPick }) {
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fetchSugg = useCallback(async () => {
    setLoading(true); setResult(null);
    try {
      const res = await fetch(`${API}/client/${token}/ai-suggest-style`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ node_id: node.id, hint: hint.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error IA");
      setResult(d);
    } catch (e) { toast.error(e.message || "Error"); }
    finally { setLoading(false); }
  }, [token, node, hint]);

  useEffect(() => { fetchSugg(); }, []);  // eslint-disable-line

  return (
    <div data-testid="ai-suggest-modal"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="w-full max-w-md bg-[#1A1A1A] border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <Sparkles size={14} className="text-[#ffc700]" />
            <h3 className="text-sm font-black text-white">Sugerencias de IA</h3>
            <span className="text-[9px] font-black px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">GRATIS</span>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-md hover:bg-white/10 flex items-center justify-center text-gray-400"><X size={14}/></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex gap-2">
            <input type="text" value={hint} onChange={e => setHint(e.target.value)}
              placeholder="'más moderno', 'minimalista', 'color azul'..."
              className="flex-1 px-3 py-2 text-[11px] bg-[#0A0A0A] border border-white/10 rounded-lg text-white"
              data-testid="ai-suggest-hint-input" />
            <button onClick={fetchSugg} disabled={loading} data-testid="ai-suggest-regen-btn"
              className="px-3 py-2 bg-[#ffc700] text-black rounded-lg text-[11px] font-bold disabled:opacity-50">
              {loading ? <Loader2 size={12} className="animate-spin" /> : "Regenerar"}
            </button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-gray-500" />
              <span className="ml-2 text-[11px] text-gray-500">Pensando variantes...</span>
            </div>
          ) : result ? (
            <div className="space-y-2">
              {(result.suggestions || []).map((s, i) => (
                <button key={i} data-testid={`ai-suggest-variant-${i}`}
                  onClick={() => { onPick(s.style); onClose(); }}
                  className="w-full text-left p-3 rounded-xl bg-white/[0.03] border border-white/5 hover:border-[#ffc700] hover:bg-[#ffc700]/5 transition-all active:scale-[0.99]">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] font-black text-white">{s.label}</span>
                    <Check size={11} className="text-[#ffc700]" />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(s.style).slice(0, 4).map(([k, v]) => (
                      <span key={k} className="text-[9px] font-mono bg-black/50 text-gray-300 px-1.5 py-0.5 rounded">
                        {k}: <span className="text-[#ffc700]">{String(v).slice(0, 22)}</span>
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ─── Right sidebar: PROPIEDADES ─────────────────────────────────────────────
function RightSidebar({
  node, draftStyle, setDraftStyle, draftAttrs, setDraftAttrs,
  ast, selectedId, onSelect, expanded, toggleExpand,
  onReorder, onDuplicate, onDelete, hasDirty, onApply, saving, onSuggestClick,
}) {
  // "editar" is the comprehensive Framer-style mega-tab. It's the default
  // when the user clicks "Editar" on the iframe overlay; "style" stays
  // for the legacy quick-edit view.
  const [tab, setTab] = useState("editar");

  // Listen for the iframe toolbar's "Editar" button — switches this panel
  // to the mega-tab even if it was on Capas/Config.
  useEffect(() => {
    const onOpen = () => setTab("editar");
    window.addEventListener("sy:open-editar-tab", onOpen);
    return () => window.removeEventListener("sy:open-editar-tab", onOpen);
  }, []);

  return (
    <aside className="w-[340px] bg-[#0A0A0A] border-l border-white/5 flex flex-col" data-testid="editor-right-sidebar">
      <div className="px-3 py-2.5 border-b border-white/5">
        <p className="text-[11px] font-black text-[#3B82F6] uppercase tracking-wider">Propiedades</p>
        <p className="text-[9.5px] text-gray-500">{node ? `<${node.tag}> · ${node.type}` : "Personalizar elemento"}</p>
      </div>
      <div className="flex border-b border-white/5">
        {[
          { id: "editar", label: "Editar" },
          { id: "style",  label: "Estilo" },
          { id: "config", label: "Config." },
          { id: "layers", label: "Capas" },
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`right-tab-${id}`}
            className={`flex-1 py-2.5 text-[10.5px] font-bold tracking-wider transition-all ${
              tab === id ? "text-[#3B82F6] border-b-2 border-[#3B82F6]" : "text-gray-500 hover:text-gray-300"
            }`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {tab === "editar" && <EditarTab node={node} draftStyle={draftStyle} setDraftStyle={setDraftStyle} draftAttrs={draftAttrs} setDraftAttrs={setDraftAttrs} />}
        {tab === "style"  && <StyleTab  node={node} draftStyle={draftStyle} setDraftStyle={setDraftStyle} onSuggestClick={onSuggestClick} />}
        {tab === "config" && <ConfigTab node={node} draftAttrs={draftAttrs} setDraftAttrs={setDraftAttrs} />}
        {tab === "layers" && (
          <LayersTab ast={ast} selectedId={selectedId} onSelect={onSelect}
            expanded={expanded} toggleExpand={toggleExpand}
            onReorder={onReorder} onDuplicate={onDuplicate} onDelete={onDelete} />
        )}
      </div>
      {node && tab !== "layers" && (
        <div className="border-t border-white/5 p-3">
          <button onClick={onApply} disabled={saving || !hasDirty} data-testid="inspector-apply-btn"
            className="w-full py-2.5 rounded-lg font-black text-[11px] transition-all active:scale-95 disabled:opacity-40"
            style={{ background: hasDirty ? "#ffc700" : "#1F1F1F", color: hasDirty ? "#000" : "#666" }}>
            {saving ? <Loader2 size={12} className="inline animate-spin mr-1"/> : hasDirty ? "Aplicar cambios" : "Sin cambios"}
          </button>
        </div>
      )}
    </aside>
  );
}

// ─── Phase 5: Contextual AI Chat panel (free) ────────────────────────────────
function ContextualAIChat({ token, selectedNode, onApply, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);  // { ops, explanation, model }
  const [history, setHistory] = useState([]);  // [{role, text}]
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const QUICK_PROMPTS = selectedNode ? [
    "Hacelo más grande y llamativo",
    "Cambialo a un tono más profesional",
    "Centralo y agregale más espacio",
  ] : [
    "Agregá un hero al principio",
    "Sumá un CTA al final con un botón fuerte",
    "Agregá una sección con 3 servicios",
  ];

  const send = async (text) => {
    const p = (text || prompt).trim();
    if (!p || loading) return;
    setLoading(true);
    setProposal(null);
    setHistory(h => [...h, { role: "user", text: p }]);
    setPrompt("");
    try {
      const res = await fetch(`${API}/client/${token}/ai-edit-context`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: p, node_id: selectedNode?.id || null }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error");
      if (!d.ops || d.ops.length === 0) {
        setHistory(h => [...h, { role: "ai", text: d.explanation || "No pude generar cambios para eso." }]);
      } else {
        setProposal(d);
        setHistory(h => [...h, { role: "ai", text: d.explanation || `${d.ops.length} cambios listos para aplicar` }]);
      }
    } catch (e) {
      toast.error(e.message || "Error de IA");
      setHistory(h => [...h, { role: "ai", text: "Hubo un error, intentá de nuevo." }]);
    } finally { setLoading(false); }
  };

  const apply = async () => {
    if (!proposal?.ops?.length) return;
    try {
      await onApply(proposal.ops);
      toast.success(`${proposal.ops.length} cambio${proposal.ops.length === 1 ? "" : "s"} aplicado${proposal.ops.length === 1 ? "" : "s"}`);
      setProposal(null);
    } catch {}
  };

  const ctxLabel = selectedNode
    ? `Editando <${selectedNode.tag}> · ${selectedNode.type}`
    : "Modo libre — agregar/eliminar/modificar lo que pidas";

  return (
    <div data-testid="ai-context-chat"
      className="fixed bottom-5 right-[340px] w-[360px] h-[500px] bg-[#0F0F0F] border border-white/10 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden"
      style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,199,0,0.2)" }}>
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-white/5 bg-gradient-to-r from-[#3D1E6B] to-[#5B2790]">
        <div className="flex items-center gap-2">
          <Sparkles size={13} className="text-[#ffc700]" />
          <h3 className="text-[12px] font-black text-white">Asistente IA</h3>
          <span className="text-[8.5px] font-black px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200">GRATIS</span>
        </div>
        <button onClick={onClose} data-testid="ai-context-close-btn"
          className="w-6 h-6 rounded hover:bg-white/15 flex items-center justify-center text-white/80">
          <X size={12} />
        </button>
      </div>
      <div className="px-3 py-1.5 bg-[#141414] border-b border-white/5">
        <p className="text-[10px] text-gray-400 truncate">{ctxLabel}</p>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2" data-testid="ai-context-history">
        {history.length === 0 && (
          <div className="space-y-1.5 pt-2">
            <p className="text-[10.5px] text-gray-500 mb-2">Probá con uno de estos:</p>
            {QUICK_PROMPTS.map((q, i) => (
              <button key={i} onClick={() => send(q)} disabled={loading}
                data-testid={`ai-quick-prompt-${i}`}
                className="w-full text-left px-2.5 py-2 text-[11px] text-gray-200 bg-white/[0.03] hover:bg-[#ffc700]/10 hover:text-white border border-white/5 hover:border-[#ffc700]/40 rounded-lg transition-colors">
                {q}
              </button>
            ))}
          </div>
        )}
        {history.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-2.5 py-1.5 rounded-lg text-[11px] leading-snug ${
              m.role === "user"
                ? "bg-[#ffc700]/15 text-white border border-[#ffc700]/30"
                : "bg-white/[0.04] text-gray-200 border border-white/5"
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            <Loader2 size={11} className="animate-spin" /> Pensando...
          </div>
        )}
        {proposal?.ops?.length > 0 && (
          <div className="mt-2 p-2.5 rounded-lg border border-emerald-500/40 bg-emerald-500/5"
            data-testid="ai-proposal-card">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10.5px] font-black text-emerald-300 uppercase tracking-wider">
                Propuesta · {proposal.ops.length} op{proposal.ops.length === 1 ? "" : "s"}
              </span>
            </div>
            <ul className="text-[10px] text-gray-300 space-y-0.5 mb-2">
              {proposal.ops.slice(0, 5).map((op, i) => (
                <li key={i} className="font-mono truncate">
                  • {op.op}{op.block ? ` (${op.block})` : ""}{op.text ? `: "${op.text.slice(0, 30)}..."` : ""}
                </li>
              ))}
            </ul>
            <div className="flex gap-1.5">
              <button onClick={apply} data-testid="ai-apply-proposal-btn"
                className="flex-1 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-black font-bold text-[11px] rounded-md flex items-center justify-center gap-1">
                <Check size={11} /> Aplicar
              </button>
              <button onClick={() => setProposal(null)} data-testid="ai-discard-proposal-btn"
                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 text-[11px] font-bold rounded-md">
                Descartar
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="border-t border-white/5 p-2 bg-[#0A0A0A]">
        <div className="flex gap-1.5">
          <input ref={inputRef} type="text" value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") send(); }}
            disabled={loading}
            placeholder={selectedNode ? "Hacé el botón más grande..." : "Agregá un hero con imagen..."}
            data-testid="ai-context-input"
            className="flex-1 px-2.5 py-2 text-[11px] bg-[#1A1A1A] border border-white/10 rounded-lg text-white placeholder:text-gray-600 focus:outline-none focus:border-[#ffc700]/40" />
          <button onClick={() => send()} disabled={loading || !prompt.trim()}
            data-testid="ai-context-send-btn"
            className="px-3 bg-[#ffc700] hover:bg-yellow-400 text-black rounded-lg disabled:opacity-40 flex items-center justify-center">
            {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Editor Page ────────────────────────────────────────────────────────
export default function EditorPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [ast, setAst] = useState(null);
  const [stats, setStats] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [siteHtml, setSiteHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [draftStyle, setDraftStyle] = useState({});
  const [draftAttrs, setDraftAttrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [showSuggest, setShowSuggest] = useState(false);
  const [showAIChat, setShowAIChat] = useState(true);  // Open by default — user request
  const [showProperties, setShowProperties] = useState(false);  // Properties panel collapsed by default
  // Resizable split for the left chat column (mirrors the old EditTab UX)
  const { chatWidth, isDragging, onDividerMouseDown, onDividerDoubleClick } = useResizableSplit();
  // Account / mods / settings (for credit-aware chat)
  const [account, setAccount] = useState(null);
  const [mods, setMods] = useState(null);
  const [settings, setSettings] = useState(null);
  const [siteSlug, setSiteSlug] = useState("");
  const [chatPreviewHtml, setChatPreviewHtml] = useState(null);
  // Phase 4: undo/redo + device toggle
  const [device, setDevice] = useState("desktop");
  const pastRef = useRef([]);    // stack of previous AST snapshots
  const futureRef = useRef([]);  // stack of redoable AST snapshots
  const [historyTick, setHistoryTick] = useState(0);  // bump to re-render undo/redo enabled state
  const HISTORY_LIMIT = 40;
  const iframeRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);  // bump to reload
  const [previewUrl, setPreviewUrl] = useState("");

  const refreshMods = useCallback(async () => {
    try {
      const r = await fetch(`${API}/client/${token}/mods/status`);
      const d = await r.json();
      if (r.ok) setMods(d);
    } catch {}
  }, [token]);

  // Initial load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [astRes, blocksRes, clientRes, modsRes] = await Promise.all([
          fetch(`${API}/client/${token}/ast`).then(r => r.json()),
          fetch(`${API}/client/${token}/blocks`).then(r => r.json()),
          fetch(`${API}/client/${token}`).then(r => r.json()),
          fetch(`${API}/client/${token}/mods/status`).then(r => r.json()).catch(() => null),
        ]);
        if (cancelled) return;
        setAst(astRes.ast); setStats(astRes.stats);
        setBlocks(blocksRes.blocks || []);
        const slug = clientRes?.site?.slug;
        if (slug) {
          setPreviewUrl(`${API}/sites/${slug}/html`);
          setSiteSlug(slug);
        }
        setAccount(clientRes?.account || null);
        setSettings(clientRes?.public_settings || null);
        if (modsRes) setMods(modsRes);
      } catch (e) { toast.error("Error cargando editor"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const nodeById = useMemo(() => {
    const m = new Map();
    const go = (ns) => ns.forEach(n => { m.set(n.id, n); if (n.children) go(n.children); });
    if (ast?.nodes) go(ast.nodes);
    return m;
  }, [ast]);
  const selectedNode = selectedId ? nodeById.get(selectedId) : null;

  useEffect(() => {
    if (!selectedNode) { setDraftStyle({}); setDraftAttrs({}); return; }
    setDraftStyle(parseStyle(selectedNode.attrs.style || ""));
    setDraftAttrs({});
  }, [selectedNode]);

  const toggleExpand = useCallback((id) => {
    setExpanded(p => ({ ...p, [id]: p[id] === false }));
  }, []);

  // Apply the new HTML returned by the backend onto the iframe DOM
  // **in-place** using morphdom — so changes (insert/delete/duplicate/move)
  // are reflected without reloading the iframe. Listeners injected by
  // `handleIframeLoad` (kill switch, dblclick, drag, selection overlay)
  // remain attached to the document and survive the morph.
  const morphIframe = useCallback((newHtml) => {
    const iframe = iframeRef.current;
    if (!iframe || !newHtml) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const parser = new DOMParser();
      const incoming = parser.parseFromString(newHtml, "text/html");
      // Preserve the editor-injected helper nodes (toolbar, drop indicator,
      // resize handles) by skipping them during morph.
      const PRESERVE_IDS = new Set(["__sy_drop_indicator", "__sy_drop_label", "__sy_drop_crosshair", "__sy_sel_toolbar"]);
      const PRESERVE_CLASSES = ["__sy-resize-handle", "__sy-grapes-handle"];
      morphdom(doc.body, incoming.body, {
        onBeforeNodeDiscarded: (node) => {
          if (node?.id && PRESERVE_IDS.has(node.id)) return false;
          const cls = node?.classList;
          if (cls && PRESERVE_CLASSES.some(c => cls.contains(c))) return false;
          return true;
        },
        onBeforeElUpdated: (fromEl, toEl) => {
          // skip identical nodes
          if (fromEl.isEqualNode(toEl)) return false;
          // Preserve the editor selection attribute / contenteditable on
          // the currently-selected element so live edit isn't interrupted.
          if (fromEl.hasAttribute?.("data-sy-selected")) {
            toEl.setAttribute("data-sy-selected", "1");
          }
          if (fromEl.getAttribute?.("contenteditable") === "true") {
            toEl.setAttribute("contenteditable", "true");
          }
          return true;
        },
      });
      // Re-position the selection overlay against the (possibly moved) node
      const win = iframe.contentWindow;
      if (win && typeof win.__syShowSelection === "function") {
        win.__syShowSelection(selectedId || null);
      }
    } catch (e) {
      // Fallback: full reload if morph blew up
      console.warn("[editor] morph failed, falling back to full reload", e);
      setIframeKey(k => k + 1);
    }
  }, [selectedId]);

  const applyOps = useCallback(async (ops) => {
    if (!ops.length) return;
    setSaving(true);
    try {
      // Phase 4: snapshot the current AST onto the undo stack BEFORE the call.
      if (ast) {
        pastRef.current.push(structuredClone(ast));
        if (pastRef.current.length > HISTORY_LIMIT) pastRef.current.shift();
        futureRef.current = [];
        setHistoryTick(t => t + 1);
      }
      const res = await fetch(`${API}/client/${token}/ast`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ops }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error");
      setAst(d.ast); setStats(d.stats);
      // In-place DOM diff — no iframe reload, native-feeling editing.
      if (d.html) morphIframe(d.html);
      else setIframeKey(k => k + 1);
      return d;
    } catch (e) {
      pastRef.current.pop();
      setHistoryTick(t => t + 1);
      toast.error(e.message || "Error aplicando");
      throw e;
    }
    finally { setSaving(false); }
  }, [token, ast, morphIframe]);

  // Phase 4: Undo & Redo via /ast/replace endpoint
  const replaceAst = useCallback(async (snapshot) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/client/${token}/ast/replace`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ast: snapshot }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error");
      setAst(snapshot); setStats(d.stats);
      if (d.html) morphIframe(d.html);
      else setIframeKey(k => k + 1);
    } catch (e) { toast.error(e.message || "Error al deshacer"); throw e; }
    finally { setSaving(false); }
  }, [token, morphIframe]);

  const onUndo = useCallback(async () => {
    if (!pastRef.current.length || !ast) return;
    const previous = pastRef.current.pop();
    futureRef.current.push(structuredClone(ast));
    if (futureRef.current.length > HISTORY_LIMIT) futureRef.current.shift();
    setHistoryTick(t => t + 1);
    try {
      await replaceAst(previous);
      toast.success("Deshecho");
    } catch {
      // restore stacks if backend failed
      pastRef.current.push(previous);
      futureRef.current.pop();
      setHistoryTick(t => t + 1);
    }
  }, [ast, replaceAst]);

  const onRedo = useCallback(async () => {
    if (!futureRef.current.length || !ast) return;
    const next = futureRef.current.pop();
    pastRef.current.push(structuredClone(ast));
    if (pastRef.current.length > HISTORY_LIMIT) pastRef.current.shift();
    setHistoryTick(t => t + 1);
    try {
      await replaceAst(next);
      toast.success("Rehecho");
    } catch {
      futureRef.current.push(next);
      pastRef.current.pop();
      setHistoryTick(t => t + 1);
    }
  }, [ast, replaceAst]);

  // Keyboard shortcuts: ⌘+Z / Ctrl+Z (undo), ⌘+⇧+Z / Ctrl+Y (redo)
  useEffect(() => {
    const onKey = (e) => {
      const target = e.target;
      // Ignore when typing in inputs/textareas/contentEditable
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); onUndo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); onRedo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onUndo, onRedo]);

  const canUndo = pastRef.current.length > 0;
  const canRedo = futureRef.current.length > 0;
  // historyTick is read here via ref-tracker so stale closures don't matter
  void historyTick;

  // Click-to-select + double-click edit + drag-and-drop drop zones
  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      if (doc.body.dataset.syEditorReady === "1") return;
      doc.body.dataset.syEditorReady = "1";

      const style = doc.createElement("style");
      style.textContent = `
        [data-sy-selected="1"] { outline: 2px solid #3B82F6 !important; outline-offset: 2px !important; }
        [data-sy-hover="1"] { outline: 1px dashed rgba(59,130,246,0.5) !important; outline-offset: 2px !important; }
        [contenteditable="true"] { outline: 2px solid #ffc700 !important; outline-offset: 2px !important; cursor: text !important; }
        body * { cursor: pointer; }
        [data-sy-drop-parent="1"] {
          outline: 2px dashed rgba(255,199,0,0.7) !important;
          outline-offset: 4px !important;
          background-color: rgba(255,199,0,0.04) !important;
          transition: outline 80ms, background-color 80ms;
        }
        #__sy_drop_indicator {
          position: absolute; height: 6px;
          background: #ffc700;
          box-shadow: 0 0 16px rgba(255,199,0,0.9), 0 0 4px rgba(255,199,0,1);
          pointer-events: none; z-index: 999999; border-radius: 3px;
          transition: top 60ms ease-out, left 60ms ease-out, width 60ms ease-out;
        }
        #__sy_drop_indicator::before, #__sy_drop_indicator::after {
          content: ""; position: absolute; top: -3px; width: 12px; height: 12px;
          border-radius: 50%; background: #ffc700;
          box-shadow: 0 0 12px rgba(255,199,0,0.9);
        }
        #__sy_drop_indicator::before { left: -6px; }
        #__sy_drop_indicator::after  { right: -6px; }
        #__sy_drop_label {
          position: absolute; pointer-events: none; z-index: 999999;
          background: #ffc700; color: #000; font: 700 10px/1 system-ui, sans-serif;
          padding: 4px 8px; border-radius: 4px; transform: translate(-50%, -130%);
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(0,0,0,0.4);
        }
        /* Selection overlay (toolbar + resize handles) */
        #__sy_sel_toolbar {
          position: absolute; z-index: 999998;
          display: none; pointer-events: auto;
          background: #1A1A1A; border: 1px solid rgba(255,255,255,0.10);
          border-radius: 8px; padding: 4px;
          font: 600 11px/1 system-ui, -apple-system, sans-serif;
          box-shadow: 0 8px 24px rgba(0,0,0,0.5);
          gap: 2px;
          transform: translateY(-100%) translateY(-8px);
          white-space: nowrap;
        }
        #__sy_sel_toolbar.visible { display: inline-flex; }
        #__sy_sel_toolbar button, #__sy_sel_toolbar .move-handle {
          background: transparent; border: 0; cursor: pointer; color: #fff;
          padding: 5px 8px; border-radius: 4px;
          display: inline-flex; align-items: center; gap: 4px;
          font: inherit;
        }
        #__sy_sel_toolbar button:hover, #__sy_sel_toolbar .move-handle:hover { background: rgba(255,255,255,0.1); }
        #__sy_sel_toolbar button.primary { background: rgba(59,130,246,0.18); color: #93C5FD; }
        #__sy_sel_toolbar button.primary:hover { background: rgba(59,130,246,0.32); color: #fff; }
        #__sy_sel_toolbar button.danger:hover { background: rgba(239,68,68,0.2); color: #fca5a5; }
        #__sy_sel_toolbar .sep { width: 1px; background: rgba(255,255,255,0.1); margin: 0 2px; align-self: stretch; }
        #__sy_sel_toolbar .move-handle { cursor: grab; user-select: none; }
        #__sy_sel_toolbar .move-handle:active { cursor: grabbing; }
        .__sy-resize-handle {
          position: absolute; z-index: 999997;
          width: 12px; height: 12px;
          background: #FFC107; border: 2px solid #fff;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
          pointer-events: auto;
        }
      `;
      doc.head.appendChild(style);

      // Selection toolbar — appears above the selected element
      const toolbar = doc.createElement("div");
      toolbar.id = "__sy_sel_toolbar";
      toolbar.innerHTML = `
        <div data-action="move" class="move-handle" title="Mover (arrastrar a otro lugar)" draggable="true">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>
          Mover
        </div>
        <span class="sep"></span>
        <button data-action="edit" class="primary" title="Editar propiedades del elemento" data-testid="toolbar-edit-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
          Editar
        </button>
        <span class="sep"></span>
        <button data-action="duplicate" title="Duplicar">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
          Duplicar
        </button>
        <button data-action="delete" class="danger" title="Eliminar">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          Eliminar
        </button>
      `;
      doc.body.appendChild(toolbar);

      // Resize: GrapesJS-ported algorithm. Handles created lazily when
      // an element is selected (so we don't litter the DOM with 8 handles
      // until needed). The attach helper returns a `dispose` fn that we
      // store + call before re-attaching for a different node.
      let activeResizerDispose = null;
      function clearResizer() {
        if (activeResizerDispose) {
          try { activeResizerDispose(); } catch {}
          activeResizerDispose = null;
        }
      }

      // Toolbar click → forward to React via postMessage
      toolbar.addEventListener("click", (e) => {
        const btn = e.target.closest("button");
        if (!btn) return;
        e.preventDefault(); e.stopPropagation();
        const action = btn.dataset.action;
        const id = toolbar.dataset.nodeid;
        if (!id) return;
        if (action === "move") return;  // drag handled separately
        window.parent.postMessage({ type: "sy-action", action, nodeId: id }, window.location.origin);
      }, true);

      // ─── Move handle — POINTER-based drag (HTML5 drag was unreliable
      // across the iframe boundary; pointer events are rock-solid). The
      // resizer uses the same pattern. Flow:
      //   pointerdown  → snapshot the target, render a ghost element
      //   pointermove  → reposition ghost, show drop indicator/crosshair
      //   pointerup    → emit sy-move-drop / sy-move-drop-absolute
      // pointercancel  → restore everything, no-op
      const moveBtn = toolbar.querySelector('.move-handle');
      let moveDrag = null;

      const cleanupMove = () => {
        if (moveDrag?.ghost) try { moveDrag.ghost.remove(); } catch {}
        indicator.style.display = "none";
        label.style.display = "none";
        crosshair.style.display = "none";
        clearParentHighlight();
        doc.body.style.cursor = "";
        doc.body.style.userSelect = "";
        // Restore handles + toolbar visibility
        if (moveDrag?.targetId && doc.defaultView.__syShowSelection) {
          doc.defaultView.__syShowSelection(moveDrag.targetId);
        }
        moveDrag = null;
      };

      moveBtn.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = toolbar.dataset.nodeid;
        if (!id) return;
        const target = doc.querySelector(`[data-sy-nodeid="${id}"]`);
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const sx = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
        const sy = doc.documentElement.scrollTop || doc.body.scrollTop || 0;

        // Build a translucent ghost that follows the cursor. Cloning the
        // node preserves visual styles so the user *sees* what they're
        // moving — much friendlier than the browser's default ghost (or
        // none at all in iframes).
        const ghost = target.cloneNode(true);
        ghost.style.cssText = `
          position: absolute;
          top: ${rect.top + sy}px; left: ${rect.left + sx}px;
          width: ${rect.width}px; height: ${rect.height}px;
          margin: 0; pointer-events: none;
          opacity: 0.55; transform: scale(0.96) rotate(-1deg);
          transform-origin: top left;
          box-shadow: 0 12px 40px rgba(255,199,7,0.45);
          outline: 2px solid #ffc700; border-radius: 6px;
          z-index: 999998;
          transition: none;
        `;
        // Ghost children should never have nodeids (would confuse findHover)
        ghost.removeAttribute("data-sy-nodeid");
        ghost.querySelectorAll("[data-sy-nodeid]").forEach(n => n.removeAttribute("data-sy-nodeid"));
        doc.body.appendChild(ghost);

        // Hide the actual element under the cursor + the toolbar/handles
        target.style.opacity = "0.25";
        toolbar.classList.remove("visible");
        doc.querySelectorAll(".__sy-grapes-handle").forEach(h => h.style.display = "none");

        moveDrag = {
          targetId: id,
          target,
          ghost,
          startX: e.clientX, startY: e.clientY,
          rect, sx, sy,
        };
        try { moveBtn.setPointerCapture(e.pointerId); } catch {}
        doc.body.style.cursor = "grabbing";
        doc.body.style.userSelect = "none";
      });

      moveBtn.addEventListener("pointermove", (e) => {
        if (!moveDrag) return;
        const sx = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
        const sy = doc.documentElement.scrollTop || doc.body.scrollTop || 0;
        const dx = e.clientX - moveDrag.startX;
        const dy = e.clientY - moveDrag.startY;
        // Position ghost at cursor (offset from original click point)
        moveDrag.ghost.style.left = `${moveDrag.rect.left + sx + dx}px`;
        moveDrag.ghost.style.top  = `${moveDrag.rect.top + sy + dy}px`;

        // Hide ghost briefly so elementFromPoint can see what's underneath
        moveDrag.ghost.style.display = "none";
        const placement = computeDropPlacement(e.clientX, e.clientY);
        moveDrag.ghost.style.display = "block";
        if (!placement) return;

        const { parentEl, indicatorBox } = placement;
        if (parentEl !== lastParentEl) {
          clearParentHighlight();
          if (parentEl && parentEl !== doc.body) {
            parentEl.setAttribute("data-sy-drop-parent", "1");
            lastParentEl = parentEl;
          }
        }
        const cs = doc.defaultView.getComputedStyle(parentEl || doc.body);
        const isFlowParent = cs.display === "flex" || cs.display === "grid" || (parentEl || doc.body) === doc.body;
        if (!isFlowParent) {
          indicator.style.display = "none";
          label.style.display = "none";
          crosshair.style.display = "block";
          crosshair.style.left = `${e.clientX + sx - 11}px`;
          crosshair.style.top  = `${e.clientY + sy - 11}px`;
        } else {
          crosshair.style.display = "none";
          indicator.style.display = "block";
          indicator.style.top = `${indicatorBox.top}px`;
          indicator.style.left = `${indicatorBox.left}px`;
          // Vertical indicator (flex-row / grid-row) → 4px wide, full child
          // height. Horizontal indicator (default) → full child width, 4px.
          if (indicatorBox.vertical) {
            indicator.style.width = `${indicatorBox.width}px`;
            indicator.style.height = `${indicatorBox.height}px`;
          } else {
            indicator.style.width = `${indicatorBox.width}px`;
            indicator.style.height = "";
          }
          label.style.display = "block";
          label.style.top = `${indicatorBox.top}px`;
          label.style.left = `${indicatorBox.left + (indicatorBox.width || 0) / 2}px`;
        }
      });

      moveBtn.addEventListener("pointerup", (e) => {
        if (!moveDrag) return;
        try { moveBtn.releasePointerCapture(e.pointerId); } catch {}
        const drag = moveDrag;
        // Hide ghost so the placement calc sees through it
        drag.ghost.style.display = "none";
        const placement = computeDropPlacement(e.clientX, e.clientY);
        if (!placement) {
          // Drop missed → restore target, no-op
          drag.target.style.opacity = "";
          cleanupMove();
          return;
        }
        const parentEl = placement.parentEl || doc.body;
        const cs = doc.defaultView.getComputedStyle(parentEl);
        const isFlowParent = cs.display === "flex" || cs.display === "grid" || parentEl === doc.body;

        if (isFlowParent) {
          window.parent.postMessage({
            type: "sy-move-drop",
            nodeId: drag.targetId,
            parentId: placement.parentId,
            index: placement.index,
          }, "*");
        } else {
          const pRect = parentEl.getBoundingClientRect();
          const x = Math.max(0, Math.round(e.clientX - pRect.left));
          const y = Math.max(0, Math.round(e.clientY - pRect.top));
          window.parent.postMessage({
            type: "sy-move-drop-absolute",
            nodeId: drag.targetId,
            parentId: placement.parentId,
            left: x, top: y,
            ensureParentRelative: cs.position === "static" || !cs.position,
          }, "*");
        }
        // Restore target opacity (the morphdom diff after the AST patch
        // will replace it anyway, but this avoids a flicker).
        drag.target.style.opacity = "";
        cleanupMove();
      });

      moveBtn.addEventListener("pointercancel", () => {
        if (moveDrag) {
          moveDrag.target.style.opacity = "";
          cleanupMove();
        }
      });

      // Belt-and-suspenders: also disable native HTML5 drag on this handle
      // so the browser doesn't try to fire ghost/dragstart events that
      // would conflict with our pointer pipeline.
      moveBtn.setAttribute("draggable", "false");
      moveBtn.addEventListener("dragstart", (e) => e.preventDefault());

      // ─── E2E test hook ───────────────────────────────────────────────────
      // Exposes a deterministic move-drop on the iframe's window so
      // playwright (which struggles with cross-frame DragEvent +
      // DataTransfer) can verify the move pipeline without simulating
      // actual mouse events. Internal use only — production users never
      // see this. Returns the postMessage payload it dispatched.
      doc.defaultView.__sy_dispatchDrop = (nodeId, opts = {}) => {
        const target = doc.querySelector(`[data-sy-nodeid="${nodeId}"]`);
        if (!target) return { error: "node-not-found" };
        const rect = target.getBoundingClientRect();
        // Default: drop just below the selected element so the test can
        // assert the new position without needing exact coords.
        const x = opts.clientX ?? (rect.left + rect.width / 2);
        const y = opts.clientY ?? (rect.bottom + 20);
        const placement = computeDropPlacement(x, y);
        if (!placement) return { error: "no-placement" };
        const payload = opts.absolute
          ? { type: "sy-move-drop-absolute", nodeId, parentId: placement.parentId,
              left: Math.max(0, Math.round(x - (placement.parentEl?.getBoundingClientRect().left || 0))),
              top:  Math.max(0, Math.round(y - (placement.parentEl?.getBoundingClientRect().top  || 0))),
              ensureParentRelative: true }
          : { type: "sy-move-drop", nodeId, parentId: placement.parentId, index: placement.index };
        // Iter91 — lock down postMessage to same origin (iframe and parent
        // are both served from this app's domain, so window.location.origin
        // resolves to the same value as the parent's origin).
        window.parent.postMessage(payload, window.location.origin);
        return payload;
      };

      // Position toolbar (and the active resizer) around the selected element.
      function positionOverlay(target) {
        if (!target) return;
        const rect = target.getBoundingClientRect();
        const sx = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
        const sy = doc.documentElement.scrollTop || doc.body.scrollTop || 0;
        toolbar.style.top = `${rect.top + sy}px`;
        toolbar.style.left = `${rect.left + sx}px`;
        if (activeResizerDispose?.update) activeResizerDispose.update();
      }

      // Keep overlay in sync on scroll/resize of the iframe
      doc.defaultView.addEventListener("scroll", () => {
        const id = toolbar.dataset.nodeid;
        if (!id) return;
        const target = doc.querySelector(`[data-sy-nodeid="${id}"]`);
        if (target) positionOverlay(target);
      }, true);

      // Expose to React via the iframe's contentWindow
      doc.defaultView.__syShowSelection = (id) => {
        clearResizer();
        if (!id) {
          toolbar.classList.remove("visible");
          toolbar.dataset.nodeid = "";
          return;
        }
        const target = doc.querySelector(`[data-sy-nodeid="${id}"]`);
        if (!target) return;
        toolbar.dataset.nodeid = id;
        toolbar.classList.add("visible");
        positionOverlay(target);
        const skip = /^(BODY|HTML|HEAD|SCRIPT|STYLE)$/.test(target.tagName);
        if (skip) return;
        // GrapesJS-ported resizer with Framer-style subtree scaling: every
        // descendant's font/padding/gap/border-radius scales with the same
        // ratio so resizing a card grows ALL its content symmetrically.
        activeResizerDispose = attachGrapesResizer(target, {
          step: 1,
          minDim: 16,
          maxDim: 4000,
          ratioDefault: false,
          enableEdges: true,
          scaleText: true,
          proportionalChildren: true,
          onMove: () => positionOverlay(target),
          onEnd: ({ stylePatch, subtreePatches }) => {
            window.parent.postMessage({
              type: "sy-resize",
              nodeId: id,
              style: stylePatch,
              subtreePatches: subtreePatches || [],
            }, "*");
          },
        });
      };

      // ─── Killswitch: disable destructive default behaviours in the iframe ─
      // Anchor navigation (tel:, mailto:, http://...), form submission, and
      // type=submit buttons would otherwise hijack any click — that's why
      // clicking a "Llamar" button popped the "Abrir Teléfono.app" dialog.
      // We use the CAPTURE phase so we always run before page handlers.
      // We DO NOT touch mousedown/mouseup so the browser still positions
      // its native caret correctly (essential for dblclick word selection).
      const NAV_TAGS = ["a", "button", "form", "input", "select", "textarea"];
      doc.addEventListener("click", (e) => {
        const inEdit = e.target.closest?.('[contenteditable="true"]');
        const navEl = e.target.closest?.(NAV_TAGS.join(","));
        // Block any link navigation / submit, regardless of contenteditable
        if (navEl) {
          e.preventDefault();
        }
        if (inEdit) return;  // editing mode → don't repost selection
        let t = e.target;
        while (t && t !== doc.body && !t.getAttribute?.("data-sy-nodeid")) t = t.parentElement;
        const id = t?.getAttribute?.("data-sy-nodeid");
        if (id) window.parent.postMessage({ type: "sy-select", nodeId: id }, window.location.origin);
      }, true);

      // Block form submission outright
      doc.addEventListener("submit", (e) => { e.preventDefault(); }, true);

      // Auxclick covers middle-click / right-click on links opening targets
      doc.addEventListener("auxclick", (e) => {
        if (e.target.closest?.("a")) e.preventDefault();
      }, true);

      // Some sites use onclick on inputs/buttons → catch them on mouseup too
      // but DON'T preventDefault on mousedown (would break native caret).
      const TEXT_TAGS = /^(H[1-6]|P|SPAN|A|BUTTON|LI|STRONG|EM|LABEL)$/;

      // Double-click → make element contenteditable AND select the word at
      // the exact click position by walking the text node character-by-char
      // (does NOT depend on Selection.modify, which is unreliable when the
      // contenteditable attribute was just toggled mid-event).
      doc.addEventListener("dblclick", (e) => {
        let t = e.target;
        while (t && t !== doc.body && !t.getAttribute?.("data-sy-nodeid")) t = t.parentElement;
        if (!t || !TEXT_TAGS.test(t.tagName)) return;
        if (t.getAttribute("contenteditable") === "true") return;

        // Capture the exact text node + offset BEFORE toggling contenteditable
        // (the toggle can re-render the element and invalidate the live caret).
        const cx = e.clientX, cy = e.clientY;
        let pinned = null;
        try {
          if (typeof doc.caretRangeFromPoint === "function") {
            const r = doc.caretRangeFromPoint(cx, cy);
            if (r && r.startContainer) pinned = { node: r.startContainer, offset: r.startOffset };
          } else if (typeof doc.caretPositionFromPoint === "function") {
            const p = doc.caretPositionFromPoint(cx, cy);
            if (p) pinned = { node: p.offsetNode, offset: p.offset };
          }
        } catch {}

        e.preventDefault();
        t.setAttribute("contenteditable", "true");
        t.dataset.syOriginalText = t.textContent || "";

        try {
          t.focus();
          const sel = doc.getSelection();

          // If we pinned a text-node + offset, expand it to the word boundary
          // by walking non-whitespace chars in BOTH directions. This is
          // deterministic: word = longest run of non-whitespace around offset.
          if (pinned && pinned.node && pinned.node.nodeType === 3 /* TEXT_NODE */) {
            const text = pinned.node.textContent || "";
            let start = Math.max(0, Math.min(pinned.offset, text.length));
            let end = start;
            while (start > 0 && /\S/.test(text[start - 1])) start--;
            while (end < text.length && /\S/.test(text[end])) end++;
            if (end > start) {
              const wordRange = doc.createRange();
              wordRange.setStart(pinned.node, start);
              wordRange.setEnd(pinned.node, end);
              sel?.removeAllRanges();
              sel?.addRange(wordRange);
              return;
            }
          }

          // Fallback: try caretRangeFromPoint again post-toggle + Selection.modify
          let range = null;
          if (typeof doc.caretRangeFromPoint === "function") {
            range = doc.caretRangeFromPoint(cx, cy);
          }
          if (range) {
            sel?.removeAllRanges();
            sel?.addRange(range);
            try {
              sel?.modify("move", "backward", "word");
              sel?.modify("extend", "forward", "word");
            } catch {}
          }
        } catch {}
      }, true);

      // On focusout from contenteditable → if text changed, notify parent
      doc.addEventListener("focusout", (e) => {
        const t = e.target;
        if (t?.getAttribute?.("contenteditable") !== "true") return;
        const newText = (t.textContent || "").trim();
        const original = t.dataset.syOriginalText || "";
        const id = t.getAttribute("data-sy-nodeid");
        t.removeAttribute("contenteditable");
        delete t.dataset.syOriginalText;
        if (id && newText && newText !== original.trim()) {
          window.parent.postMessage({ type: "sy-text-change", nodeId: id, text: newText }, window.location.origin);
        }
      }, true);

      // Hover highlight
      let lastHover = null;
      doc.addEventListener("mouseover", (e) => {
        let t = e.target;
        while (t && t !== doc.body && !t.getAttribute?.("data-sy-nodeid")) t = t.parentElement;
        if (lastHover) lastHover.removeAttribute("data-sy-hover");
        if (t && t !== doc.body) { t.setAttribute("data-sy-hover", "1"); lastHover = t; }
      }, true);
      doc.addEventListener("mouseleave", () => {
        if (lastHover) { lastHover.removeAttribute("data-sy-hover"); lastHover = null; }
      }, true);

      // ── Drag-and-drop: detect cross-frame drops of new blocks ───────────
      let indicator = doc.getElementById("__sy_drop_indicator");
      if (!indicator) {
        indicator = doc.createElement("div");
        indicator.id = "__sy_drop_indicator";
        indicator.style.display = "none";
        doc.body.appendChild(indicator);
      }
      let label = doc.getElementById("__sy_drop_label");
      if (!label) {
        label = doc.createElement("div");
        label.id = "__sy_drop_label";
        label.style.display = "none";
        label.textContent = "Soltá aquí";
        doc.body.appendChild(label);
      }

      // Find the deepest sy-nodeid element under (clientX, clientY).
      // Falls back to body if nothing matches. Skips the drop indicator itself.
      const findHover = (clientX, clientY) => {
        const stack = doc.elementsFromPoint(clientX, clientY) || [];
        for (const el of stack) {
          if (!el || el === indicator || el === label || el === crosshair) continue;
          if (el.id === "__sy_drop_indicator" || el.id === "__sy_drop_label" || el.id === "__sy_drop_crosshair") continue;
          if (el.id === "__sy_sel_toolbar" || el.closest?.("#__sy_sel_toolbar")) continue;
          if (el.classList?.contains("__sy-grapes-handle")) continue;
          if (el.classList?.contains("__sy-resize-handle")) continue;
          // Walk up to the nearest sy-nodeid ancestor
          let cur = el;
          while (cur && cur !== doc.body && !cur.getAttribute?.("data-sy-nodeid")) cur = cur.parentElement;
          if (cur && cur !== doc.body) return cur;
        }
        return null;
      };

      // Decide: insert before/after the hovered sibling, or as last child of an empty container.
      // Returns { parentEl, parentId, index, indicatorBox: {top,left,width} }
      const computeDropPlacement = (clientX, clientY) => {
        const sx = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
        const sy = doc.documentElement.scrollTop || doc.body.scrollTop || 0;
        const hovered = findHover(clientX, clientY);

        // Helper to build indicator box from a target rect + position
        const lineFromRect = (rect, position /* "top" | "bottom" | "left" | "right" */) => {
          if (position === "left" || position === "right") {
            return {
              top: rect.top + sy,
              left: (position === "left" ? rect.left : rect.right) + sx - 2,
              width: 4,
              height: rect.height,
              vertical: true,
            };
          }
          return {
            top: (position === "top" ? rect.top : rect.bottom) + sy - 3,
            left: rect.left + sx,
            width: rect.width,
          };
        };

        // ─── Resolve the parent we're dropping into ──────────────────────
        let parentEl;
        if (!hovered) {
          parentEl = doc.body;
        } else {
          // GrapesJS-inspired: if hovered IS a container and the cursor is
          // in its inner buffer zone (>15px from each edge), treat the
          // CONTAINER as the parent (drop INSIDE).
          const r = hovered.getBoundingClientRect();
          const isContainer = /^(DIV|SECTION|HEADER|FOOTER|MAIN|ARTICLE|ASIDE|UL|OL|FORM|FIGURE|NAV)$/.test(hovered.tagName);
          const innerKids = isContainer
            ? Array.from(hovered.children).filter(c => c.getAttribute?.("data-sy-nodeid"))
            : [];
          const buffer = 15;
          const insideX = clientX > r.left + buffer && clientX < r.right - buffer;
          const insideY = clientY > r.top + buffer && clientY < r.bottom - buffer;
          if (isContainer && (innerKids.length === 0 || (insideX && insideY))) {
            parentEl = hovered;
          } else {
            parentEl = hovered.parentElement || doc.body;
          }
        }

        // ─── Use grapesjs findPosition over the parent's children ────────
        // This is THE drop-detection brain ported from GrapesJS sorter
        // SorterUtils.findPosition. Handles flex rows, grid rows, and
        // vertical lists uniformly. See /app/frontend/src/utils/grapesFindPosition.js
        const dims = collectDims(parentEl, doc.defaultView);
        if (dims.length === 0) {
          // Empty parent — drop as first child
          const r = parentEl.getBoundingClientRect();
          return {
            parentEl,
            parentId: parentEl === doc.body ? "" : (parentEl.getAttribute?.("data-sy-nodeid") || ""),
            index: 0,
            indicatorBox: {
              top: r.top + sy + 6,
              left: r.left + sx + 6,
              width: Math.max(40, r.width - 12),
            },
          };
        }
        const { index, placement } = findPosition(dims, clientX, clientY);
        const targetDim = dims[index];
        const targetRect = targetDim.el.getBoundingClientRect();
        const insertIndex = placement === "before" ? index : index + 1;
        const parentId = parentEl === doc.body ? "" : (parentEl.getAttribute?.("data-sy-nodeid") || "");

        // For horizontal flow (flex row / grid row), draw a vertical line on
        // the left or right edge of the target element. For vertical flow,
        // draw the classic horizontal line on top/bottom.
        const indicatorBox = targetDim.dir
          ? lineFromRect(targetRect, placement === "before" ? "top" : "bottom")
          : lineFromRect(targetRect, placement === "before" ? "left" : "right");

        return { parentEl, parentId, index: insertIndex, indicatorBox };
      };

      let lastParentEl = null;
      const clearParentHighlight = () => {
        if (lastParentEl) {
          lastParentEl.removeAttribute("data-sy-drop-parent");
          lastParentEl = null;
        }
      };

      // Free-form crosshair shown on free positioning (non-flex/grid parent
      // when MOVING an existing node). Acts as a visual cursor target.
      let crosshair = doc.getElementById("__sy_drop_crosshair");
      if (!crosshair) {
        crosshair = doc.createElement("div");
        crosshair.id = "__sy_drop_crosshair";
        crosshair.style.cssText = `
          position: absolute; width: 22px; height: 22px;
          border: 2px solid #ffc700;
          background: rgba(255,199,7,0.18);
          border-radius: 50%;
          pointer-events: none; z-index: 999999;
          box-shadow: 0 0 16px rgba(255,199,7,0.7);
          display: none;
        `;
        doc.body.appendChild(crosshair);
      }

      doc.addEventListener("dragover", (e) => {
        const types = Array.from(e.dataTransfer.types);
        if (!types.includes("application/sy-block") && !types.includes("application/sy-move-node")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = types.includes("application/sy-move-node") ? "move" : "copy";
        const placement = computeDropPlacement(e.clientX, e.clientY);
        if (!placement) return;
        const { parentEl, indicatorBox } = placement;
        if (parentEl !== lastParentEl) {
          clearParentHighlight();
          if (parentEl && parentEl !== doc.body) {
            parentEl.setAttribute("data-sy-drop-parent", "1");
            lastParentEl = parentEl;
          }
        }
        // Choose between slot-line (flex/grid/body) and free-form crosshair
        // (block container) for MOVE drags only. Block presets always slot.
        const isMove = types.includes("application/sy-move-node");
        const cs = doc.defaultView.getComputedStyle(parentEl || doc.body);
        const isFlowParent = cs.display === "flex" || cs.display === "grid" || (parentEl || doc.body) === doc.body;
        if (isMove && !isFlowParent) {
          indicator.style.display = "none";
          label.style.display = "none";
          const sx = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
          const sy = doc.documentElement.scrollTop || doc.body.scrollTop || 0;
          crosshair.style.display = "block";
          crosshair.style.left = `${e.clientX + sx - 11}px`;
          crosshair.style.top  = `${e.clientY + sy - 11}px`;
        } else {
          crosshair.style.display = "none";
          indicator.style.display = "block";
          indicator.style.top = `${indicatorBox.top}px`;
          indicator.style.left = `${indicatorBox.left}px`;
          indicator.style.width = `${indicatorBox.width}px`;
          label.style.display = "block";
          label.style.top = `${indicatorBox.top}px`;
          label.style.left = `${indicatorBox.left + indicatorBox.width / 2}px`;
        }
      });
      doc.addEventListener("dragleave", (e) => {
        if (e.relatedTarget) return;
        indicator.style.display = "none";
        label.style.display = "none";
        crosshair.style.display = "none";
        clearParentHighlight();
      });
      doc.addEventListener("drop", (e) => {
        const blockKey = e.dataTransfer.getData("application/sy-block");
        const moveId = e.dataTransfer.getData("application/sy-move-node");
        if (!blockKey && !moveId) return;
        e.preventDefault();
        indicator.style.display = "none";
        label.style.display = "none";
        crosshair.style.display = "none";
        clearParentHighlight();
        const placement = computeDropPlacement(e.clientX, e.clientY);
        if (!placement) return;
        if (moveId) {
          // Free-form positioning: drop the moved node at the EXACT cursor
          // coordinates inside its new parent. We calculate the target
          // parent's offset rect and the cursor's (x,y) inside it, then
          // tell the host to switch the node to position:absolute and pin
          // it at left/top from the drop. Fallback to slot-drop (insert at
          // computed index) when the parent is body or a flex/grid lane.
          const parentEl = placement.parentEl || doc.body;
          const cs = doc.defaultView.getComputedStyle(parentEl);
          const isFlowParent = cs.display === "flex" || cs.display === "grid" || parentEl === doc.body;
          if (!isFlowParent) {
            const pRect = parentEl.getBoundingClientRect();
            const px = doc.documentElement.scrollLeft || doc.body.scrollLeft || 0;
            const py = doc.documentElement.scrollTop || doc.body.scrollTop || 0;
            const x = Math.max(0, Math.round(e.clientX - pRect.left));
            const y = Math.max(0, Math.round(e.clientY - pRect.top));
            // Make sure the parent is positioned so absolute children pin
            // relative to it. The host will set this via update_style on
            // the parent if it's currently static.
            const parentStaticPosition = cs.position === "static" || !cs.position;
            window.parent.postMessage({
              type: "sy-move-drop-absolute",
              nodeId: moveId,
              parentId: placement.parentId,
              left: x, top: y,
              ensureParentRelative: parentStaticPosition,
            }, "*");
            return;
          }
          window.parent.postMessage({
            type: "sy-move-drop",
            nodeId: moveId,
            parentId: placement.parentId,
            index: placement.index,
          }, "*");
        } else {
          window.parent.postMessage({
            type: "sy-drop",
            blockKey,
            parentId: placement.parentId,
            index: placement.index,
          }, "*");
        }
      });
    } catch (err) { /* cross-origin ignored */ }
  }, []);

  // Receive postMessage from iframe (sy-select / sy-drop / sy-text-change)
  useEffect(() => {
    // Dedupe ref — prevents the same resize commit from triggering two
    // toasts when React.StrictMode or the iframe double-fires pointerup.
    const recentResize = { key: "", ts: 0 };
    const onMsg = async (e) => {
      const msg = e.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "sy-select" && msg.nodeId) {
        setSelectedId(msg.nodeId);
        return;
      }
      if (msg.type === "sy-drop" && msg.blockKey) {
        try {
          await applyOps([{
            op: "insert_block",
            block: msg.blockKey,
            parent_id: msg.parentId || "",
            index: typeof msg.index === "number" ? msg.index : -1,
          }]);
          toast.success("Bloque insertado");
        } catch {}
        return;
      }
      if (msg.type === "sy-text-change" && msg.nodeId && typeof msg.text === "string") {
        try {
          await applyOps([{ op: "update_text", node_id: msg.nodeId, text: msg.text }]);
          toast.success("Texto actualizado");
        } catch {}
        return;
      }
      // Selection action toolbar (Eliminar / Duplicar)
      if (msg.type === "sy-action" && msg.nodeId) {
        if (msg.action === "edit") {
          // Open the right Properties panel and reveal the Editar mega-tab.
          // Close the AI chat at the same time so the Properties panel gets
          // the full width of the canvas — the chat collapses to the
          // floating yellow AIChatBubble (still one click away).
          setShowProperties(true);
          setSelectedId(msg.nodeId);
          setShowAIChat(false);
          try { window.dispatchEvent(new CustomEvent("sy:open-editar-tab")); } catch {}
          return;
        }
        const op = msg.action === "delete" ? "delete_node"
                 : msg.action === "duplicate" ? "duplicate_node"
                 : null;
        if (!op) return;
        try {
          await applyOps([{ op, node_id: msg.nodeId }]);
          if (op === "delete_node") {
            setSelectedId(null);
            toast.success("Eliminado");
          } else {
            toast.success("Duplicado");
          }
        } catch {}
        return;
      }
      // Live resize commit (after mouseup on a corner handle).
      // Batches the parent's style + every descendant's subtree patch
      // into a single PATCH /ast call so the whole subtree scales atomically.
      if (msg.type === "sy-resize" && msg.nodeId && msg.style) {
        // Dedupe: ignore identical commits within a 250ms window.
        const dedupeKey = `${msg.nodeId}|${msg.style.width || ""}|${msg.style.height || ""}|${(msg.subtreePatches || []).length}`;
        const now = Date.now();
        if (recentResize.key === dedupeKey && now - recentResize.ts < 250) return;
        recentResize.key = dedupeKey;
        recentResize.ts = now;

        const ops = [{ op: "update_style", node_id: msg.nodeId, style: msg.style }];
        if (Array.isArray(msg.subtreePatches)) {
          for (const p of msg.subtreePatches) {
            if (p?.nodeId && p?.style && Object.keys(p.style).length) {
              ops.push({ op: "update_style", node_id: p.nodeId, style: p.style });
            }
          }
        }
        try {
          await applyOps(ops);
          toast.success(ops.length > 1 ? `Escalado (${ops.length} elementos)` : "Tamaño actualizado");
        } catch {}
        return;
      }
      // Move existing node (drag from selection toolbar to new spot)
      if (msg.type === "sy-move-drop" && msg.nodeId) {
        try {
          await applyOps([{
            op: "move_node",
            node_id: msg.nodeId,
            parent_id: msg.parentId || "",
            index: typeof msg.index === "number" ? msg.index : -1,
          }]);
          toast.success("Movido");
        } catch (err) {
          // Fallback if backend doesn't support move_node yet → delete + insert
          toast.error("No se pudo mover (intentá Cortar / Pegar)");
        }
        return;
      }
      // Free-form move: drop the node at the exact (left,top) inside the
      // new parent, switching its position to absolute. If the parent is
      // currently static, also bump it to position:relative so the child
      // pins correctly. All in one batched PATCH /ast call.
      if (msg.type === "sy-move-drop-absolute" && msg.nodeId) {
        const ops = [{
          op: "move_node",
          node_id: msg.nodeId,
          parent_id: msg.parentId || "",
          index: -1,  // append, doesn't matter since we'll absolute-position
        }, {
          op: "update_style",
          node_id: msg.nodeId,
          style: {
            position: "absolute",
            left: `${msg.left}px`,
            top: `${msg.top}px`,
            // Don't reset margin — preserves any author offset the element
            // already had. If the user wants to clear it, they can do so
            // from the Editar tab.
          },
        }];
        if (msg.ensureParentRelative && msg.parentId) {
          ops.push({
            op: "update_style",
            node_id: msg.parentId,
            style: { position: "relative" },
          });
        }
        try {
          await applyOps(ops);
          toast.success("Posicionado");
        } catch (err) {
          toast.error("No se pudo posicionar — el elemento volvió a su lugar");
        }
        return;
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [applyOps]);

  // Re-apply selection highlight in the iframe when selectedId changes.
  // Also tells the iframe-injected overlay (toolbar + resize handles) to
  // appear over the selected element.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      doc.querySelectorAll('[data-sy-selected="1"]').forEach(el => el.removeAttribute("data-sy-selected"));
      if (selectedId) {
        const el = doc.querySelector(`[data-sy-nodeid="${selectedId}"]`);
        if (el) el.setAttribute("data-sy-selected", "1");
      }
      // Update overlay
      const win = iframe.contentWindow;
      if (win && typeof win.__syShowSelection === "function") {
        win.__syShowSelection(selectedId || null);
      }
    } catch {}
  }, [selectedId, iframeKey]);

  // ─── LIVE PREVIEW ───────────────────────────────────────────────────────
  // Reflect draftStyle changes onto the iframe element instantly (no backend
  // round-trip). Backend commit still happens through "Aplicar".
  useEffect(() => {
    if (!selectedId || !selectedNode) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      const el = doc.querySelector(`[data-sy-nodeid="${selectedId}"]`);
      if (!el) return;
      // Cache the original cssText once so we can restore on selection change.
      if (el.dataset.syOriginalStyle === undefined) {
        el.dataset.syOriginalStyle = el.getAttribute("style") || "";
      }
      const merged = { ...parseStyle(el.dataset.syOriginalStyle || ""), ...draftStyle };
      // Remove keys that were explicitly cleared (empty string).
      const out = Object.entries(merged)
        .filter(([, v]) => v !== "" && v != null)
        .map(([k, v]) => `${k}: ${v}`)
        .join("; ");
      el.setAttribute("style", out);
    } catch {}
  }, [draftStyle, selectedId, selectedNode, iframeKey]);

  // When the user picks a different node, drop the stale syOriginalStyle cache
  // on the previously edited element so the next selection captures fresh state.
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument;
      if (!doc) return;
      doc.querySelectorAll('[data-sy-original-style]').forEach(el => {
        if (el.getAttribute("data-sy-nodeid") !== selectedId) {
          delete el.dataset.syOriginalStyle;
        }
      });
    } catch {}
  }, [selectedId]);

  // Apply button diff calculation. Uses an epsilon for px/em/rem numeric
  // values so 54.60px and 54.6px aren't considered different (which would
  // either spuriously enable Apply or mask real changes after a resize).
  const styleValuesEqual = (a, b) => {
    if (a === b) return true;
    const ma = (a || "").match(/^(-?[\d.]+)(px|em|rem|%)$/i);
    const mb = (b || "").match(/^(-?[\d.]+)(px|em|rem|%)$/i);
    if (!ma || !mb || ma[2].toLowerCase() !== mb[2].toLowerCase()) return false;
    return Math.abs(parseFloat(ma[1]) - parseFloat(mb[1])) < 0.05;
  };
  const hasDirty = Object.keys(draftStyle).length > 0 || Object.keys(draftAttrs).length > 0 ?
    (() => {
      if (!selectedNode) return false;
      const orig = parseStyle(selectedNode.attrs.style || "");
      for (const k of new Set([...Object.keys(orig), ...Object.keys(draftStyle)])) {
        if (!styleValuesEqual(orig[k] || "", draftStyle[k] || "")) return true;
      }
      for (const [k, v] of Object.entries(draftAttrs)) {
        if ((selectedNode.attrs[k] || "") !== v) return true;
      }
      return false;
    })() : false;

  const onApply = async () => {
    if (!selectedNode) return;
    const ops = [];
    const orig = parseStyle(selectedNode.attrs.style || "");
    const sDiff = {};
    for (const k of new Set([...Object.keys(orig), ...Object.keys(draftStyle)])) {
      if (!styleValuesEqual(orig[k] || "", draftStyle[k] || "")) sDiff[k] = draftStyle[k] || "";
    }
    if (Object.keys(sDiff).length) ops.push({ op: "update_style", node_id: selectedNode.id, style: sDiff });
    const aDiff = {};
    for (const [k, v] of Object.entries(draftAttrs)) {
      if ((selectedNode.attrs[k] || "") !== v) aDiff[k] = v;
    }
    if (Object.keys(aDiff).length) ops.push({ op: "update_attrs", node_id: selectedNode.id, attrs: aDiff });
    if (!ops.length) return;
    try {
      const d = await applyOps(ops);
      toast.success(`${d.applied_ops} cambio${d.applied_ops === 1 ? "" : "s"} aplicado${d.applied_ops === 1 ? "" : "s"}`);
    } catch {}
  };

  const onReorder = useCallback(async (parentId, order) => {
    try { await applyOps([{ op: "reorder_children", parent_id: parentId, order }]); toast.success("Reordenado"); } catch {}
  }, [applyOps]);
  const onDuplicate = useCallback(async (id) => {
    try { await applyOps([{ op: "duplicate_node", node_id: id }]); toast.success("Duplicado"); } catch {}
  }, [applyOps]);
  const onDelete = useCallback(async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("¿Eliminar este elemento?")) return;
    try { await applyOps([{ op: "delete_node", node_id: id }]); if (selectedId === id) setSelectedId(null); toast.success("Eliminado"); } catch {}
  }, [applyOps, selectedId]);
  const onInsertBlock = useCallback(async (blockKey) => {
    try { await applyOps([{ op: "insert_block", block: blockKey, parent_id: "", index: -1 }]); toast.success("Bloque agregado"); setActiveCat(null); } catch {}
  }, [applyOps]);
  const onPickSuggestion = useCallback((styleDict) => {
    setDraftStyle(prev => ({ ...prev, ...styleDict }));
    toast.success("Variante seleccionada — revisá y aplicá");
  }, []);

  const onPublish = async () => {
    setPublishing(true);
    try {
      const res = await fetch(`${API}/client/${token}/deploy`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Error publicando");
      toast.success("¡Publicado en Cloudflare!");
    } catch (e) { toast.error(e.message || "Error publicando"); }
    finally { setPublishing(false); }
  };
  const onPreview = () => { if (previewUrl) window.open(previewUrl, "_blank"); };

  if (loading) {
    return (
      <div className="h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#ffc700]" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col md:flex-row bg-[#0A0A0A] text-white overflow-hidden" data-testid="editor-page">
      {/* Iter88 — Mobile editor block.
          Drag/drop + the dense properties panel are hopeless on a phone;
          letting clients into the editor on mobile invites accidental damage.
          We keep the sidebar/bottom-nav so they can still reach Stats /
          Domain / Plan, but the editor canvas itself is replaced by a
          friendly "open from desktop" gate. Sits ABOVE the editor (z-50)
          but BELOW the bottom-nav (which has its own z-index). */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-[50] flex flex-col items-center justify-center px-6 text-center bg-[#0A0A0A]" style={{ bottom: "calc(56px + env(safe-area-inset-bottom, 0px))" }} data-testid="editor-mobile-block">
        <div className="w-16 h-16 rounded-2xl bg-[#FFC107]/10 border border-[#FFC107]/30 flex items-center justify-center mb-5">
          <Monitor size={28} className="text-[#FFC107]" />
        </div>
        <h2 className="text-xl font-black text-white mb-2 leading-tight">
          Editor solo desde escritorio
        </h2>
        <p className="text-sm text-gray-400 leading-relaxed max-w-sm mb-6">
          Para evitar tocar partes del diseño por accidente, el editor drag-and-drop está disponible <span className="text-[#FFC107] font-bold">solo desde una computadora</span>.
        </p>
        <p className="text-xs text-gray-500 leading-relaxed max-w-sm mb-8">
          Desde el celular podés ver tus estadísticas, manejar tu dominio y plan, e invitar referidos.
        </p>
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <a
            href={siteSlug ? `/pv/${siteSlug}` : "#"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-block-view-site"
            className="flex items-center justify-center gap-2 py-3 bg-white/[0.07] border border-white/[0.1] rounded-xl text-sm font-bold text-white hover:bg-white/[0.12] transition-all"
          >
            Ver mi sitio publicado
          </a>
          <button
            onClick={() => navigate(`/client/${token}?tab=analytics`)}
            data-testid="mobile-block-go-stats"
            className="flex items-center justify-center gap-2 py-3 bg-[#FFC107] text-black rounded-xl text-sm font-black hover:bg-[#FFD54F] transition-all"
          >
            Ir a estadísticas
          </button>
        </div>
        {/* Bottom-nav stays visible underneath via z-index ordering — this
            modal sits above the editor but the nav (z-40) shows through. */}
      </div>

      {/* Client navigation sidebar — first column, top-left logo + tabs.
          Becomes a bottom bar on mobile via its own responsive variants. */}
      <ClientNavSidebar
        activeTab="edit"
        onNavigate={(tab) => {
          if (tab === "edit") return;  // already here
          navigate(`/client/${token}?tab=${tab}`);
        }}
        previewUrl={siteSlug ? `/pv/${siteSlug}` : null}
      />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0 pb-16 md:pb-0">
        <EditorHeader onPublish={onPublish} publishing={publishing} onPreview={onPreview}
          onUndo={onUndo} onRedo={onRedo} canUndo={canUndo} canRedo={canRedo}
          device={device} setDevice={setDevice} />
        <div className="flex-1 flex overflow-hidden relative">

          {/* ── LEFT COLUMN: Resizable AI Chat (credits) ─────────────────── */}
          {showAIChat && (
            <>
              <div
                className="flex-shrink-0 h-full bg-white relative w-full md:w-[var(--chat-w,420px)]"
                style={{ "--chat-w": `${chatWidth}px` }}
                data-testid="chat-column"
              >
                <EmbeddedAIChat
                  token={token}
                  account={account}
                  mods={mods}
                  onModsRefresh={refreshMods}
                  siteSlug={siteSlug}
                  offerExpiresAt={account?.offer_expires_at}
                  settings={settings}
                  open={showAIChat}
                  onClose={() => setShowAIChat(false)}
                  applyOps={applyOps}
                  onUndo={onUndo}
                  onIframeReload={async () => {
                    setIframeKey(k => k + 1);
                    try {
                      const r = await fetch(`${API}/client/${token}/ast?force_regenerate=true`);
                      const d = await r.json();
                      if (r.ok) { setAst(d.ast); setStats(d.stats); pastRef.current = []; futureRef.current = []; }
                    } catch {}
                  }}
                  onPreviewHtml={setChatPreviewHtml}
                />
              </div>
              {/* Resizable divider — desktop only, no resize on mobile */}
              <div
                data-testid="chat-divider"
                onMouseDown={onDividerMouseDown}
                onDoubleClick={onDividerDoubleClick}
                className={`hidden md:block w-1.5 flex-shrink-0 cursor-col-resize relative z-30 select-none transition-colors ${
                  isDragging ? "bg-[#6D28D9]" : "bg-[#1A1A1A] hover:bg-[#6D28D9]/60"
                }`}
                title="Arrastrá para redimensionar · Doble clic para reset"
              >
                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex flex-col gap-[2px] items-center justify-center">
                  {[0,1,2,3].map(i => (
                    <span key={i} className={`w-[2px] h-[2px] rounded-full ${isDragging ? "bg-white" : "bg-gray-500"}`} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Editor blocks sidebar (desktop only — too narrow for mobile) ── */}
          <div className="hidden md:contents">
            <EditorBlocksSidebar
              activeCat={activeCat}
              onCatClick={(id) => setActiveCat(activeCat === id ? null : id)} />
            {activeCat && (
              <BlocksDrawer category={activeCat} blocks={blocks}
                onInsert={(k) => { onInsertBlock(k); setActiveCat(null); }}
                onClose={() => setActiveCat(null)}
                offsetLeft={(showAIChat ? chatWidth + 6 : 0) + 72} />
            )}
          </div>

          {/* ── CANVAS ─ hidden on mobile when chat is open (chat takes full screen) ── */}
          <main
            className={`${showAIChat ? "hidden md:flex" : "flex"} flex-1 overflow-auto bg-[#0D0D0D] items-start justify-center p-2 md:p-6 min-w-0`}
            data-testid="editor-canvas"
          >
            <div
              className="h-full rounded-xl overflow-hidden border border-white/10 bg-white shadow-2xl transition-all duration-300"
              style={{
                width: DEVICES.find(d => d.id === device)?.width || "100%",
                maxWidth: DEVICES.find(d => d.id === device)?.maxWidth || 1200,
              }}
              data-testid={`canvas-frame-${device}`}
            >
              {previewUrl ? (
                <iframe
                  key={iframeKey}
                  ref={iframeRef}
                  src={`${previewUrl}${previewUrl.includes("?") ? "&" : "?"}v=${iframeKey}`}
                  onLoad={handleIframeLoad}
                  title="preview"
                  className="w-full h-full"
                  data-testid="editor-preview-iframe"
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">Sin sitio</div>
              )}
            </div>
          </main>

          {/* Properties: desktop only — too dense for mobile screens */}
          <div className="hidden md:contents">
            {showProperties ? (
              <div className="flex h-full" data-testid="properties-column">
                <button
                  onClick={() => setShowProperties(false)}
                  title="Colapsar Propiedades"
                  data-testid="props-toggle-btn"
                  className="w-6 flex-shrink-0 bg-[#0A0A0A] border-l border-white/5 hover:bg-white/5 flex items-center justify-center text-gray-400 hover:text-white"
                >
                  <ChevronRight size={14} />
                </button>
                <RightSidebar
                  node={selectedNode}
                  draftStyle={draftStyle} setDraftStyle={setDraftStyle}
                  draftAttrs={draftAttrs} setDraftAttrs={setDraftAttrs}
                  ast={ast} selectedId={selectedId} onSelect={setSelectedId}
                  expanded={expanded} toggleExpand={toggleExpand}
                  onReorder={onReorder} onDuplicate={onDuplicate} onDelete={onDelete}
                  hasDirty={hasDirty} onApply={onApply} saving={saving}
                  onSuggestClick={() => selectedNode && setShowSuggest(true)}
                />
              </div>
            ) : (
              <button
                onClick={() => setShowProperties(true)}
                title="Abrir Propiedades"
                data-testid="props-toggle-btn"
                className="w-9 flex-shrink-0 bg-[#0A0A0A] border-l border-white/5 hover:bg-white/5 flex flex-col items-center justify-center gap-1.5 text-gray-400 hover:text-[#3B82F6] transition-colors"
              >
                <ChevronRight size={14} className="rotate-180" />
                <span className="[writing-mode:vertical-rl] rotate-180 text-[9.5px] font-black tracking-wider uppercase">
                  Propiedades
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {!showAIChat && <AIChatBubble onOpen={() => setShowAIChat(true)} />}
      {showSuggest && selectedNode && (
        <StyleSuggestModal node={selectedNode} token={token}
          onClose={() => setShowSuggest(false)} onPick={onPickSuggestion} />
      )}
    </div>
  );
}
